<template>
  <div class="conversation-player" :class="{ 'is-playing-mode': isPlaying }">
    <play-button-overlay
      v-if="isSharedView && !hasStartedPlaying"
      :visible="!hasStartedPlaying"
      :title="conversationTitle"
      :description="conversationDescription"
      :thumbnail="conversationThumbnail"
      :shared-id="sharedId"
      @play="handleOverlayPlay"
    />
    <!-- Two column layout wrapper -->
    <div class="player-layout">
      <!-- Left Column - Metadata & Controls -->
      <podcast-controls
        v-if="isMobile"
        :is-mobile="isMobile"
        :is-pro="isPro"
        :is-playing="isPlaying"
        :is-expanded-view="isExpandedView"
        @toggle-expanded-view="toggleExpandedView"
        :active-segment="activeSegment"
        :total-segments="parsedConversation.length"
        :segments="parsedConversation"
        :current-time="currentTime"
        :estimated-length="formattedLength"
        :voice-speed="voiceSpeed"
        :progress-percentage="progressPercentage"
        :is-downloading="isDownloading"
        :download-progress="downloadProgress"
        :download-ready="downloadReady"
        :is-muted="false"
        :showing-documents="showDocumentsInsteadOfSpeakers"
        :current-speaker="currentSpeaker"
        @toggle-play="togglePlay"
        @skip-backward="skipBackward"
        @skip-forward="skipForward"
        @seek="seek"
        @speed-change="onSpeedChange"
        @download="downloadFullConversation"
        @trigger-download="triggerDownload"
      />
      <div class="metadata-column" :class="{ 'is-playing': isPlaying, 'is-expanded': isExpandedView }" v-show="!isPlaying && !isMobile">
        <!-- Document Segment Mapper - Add this component -->
        <document-segment-mapper
          v-show="!isPlaying"
          :is-mobile="isMobile"
          :is-pro="isPro"
          ref="documentMapper"
          :last-document-update="lastDocumentUpdate"
          :document-data="documentData"
          :message-id="messageId"
          :segments="parsedConversation"
          :active-segment-index="activeSegment"
          :uploaded-documents="uploadedDocuments"
          @segment-selected="jumpToSegment"
          @mapping-updated="handleMappingUpdated"
        />
        <!-- Player Header -->
        <div class="player-header">
        </div>
      </div>

      <div class="speaker-column" :class="{ 'is-playing': isPlaying, 'is-expanded': isExpandedView }">
        <div v-if="isPlaying" class="speaker-image-overlay">
          <img
            :src="getActiveSpeakerImageUrl()"
            :alt="currentSpeaker + ' image'"
            class="speaker-image"
          />
          <div class="speaker-info-container">
            <div class="speaker-name">{{ currentSpeaker }}</div>
            <div class="speaker-role" v-if="parsedConversation[activeSegment]">
              {{ parsedConversation[activeSegment].role || 'Speaker' }}
            </div>
          </div>
          <div class="speaker-wave">
            <div class="wave-bar" v-for="n in 3" :key="`wave-${n}`"></div>
          </div>
        </div>
        <!-- Citation Panel (when citations exist) -->
          <!-- Search Image Panel - (when images exist) -->
          <segment-image-panel
            :search-results="searchResults"
            :active-segment="activeSegment"
            :total-segments="totalSegments"
            :show-citation-panel="showCitationPanel"
            :show-documents-panel="showDocumentsInsteadOfSpeakers"
            :voice-speed="voiceSpeed"
            :is-playing="isPlaying"
            :active-segment-content="activeSegmentDisplayText"
            :active-segment-speaker="currentSpeaker"
            :speaker-image="getActiveSpeakerImageUrl()"
            @image-visibility-changed="onImageVisibilityChanged"
            ref="segmentImagePanel"
        />

        <!-- Document Display - (when documents exist) -->
        <document-display
          v-show="showDocumentsInsteadOfSpeakers"
          :documents="currentSegmentDocuments"
          :is-playing="isPlaying"
          :voice-speed="voiceSpeed"
          :active-segment-content="activeSegmentDisplayText"
          :active-segment-speaker="currentSpeaker"
          :speaker-image="getActiveSpeakerImageUrl()"
        />
        <podcast-controls
          v-if="!isMobile && isPlaying"
          :is-mobile="isMobile"
          :is-pro="isPro"
          :is-playing="isPlaying"
          :is-expanded-view="isExpandedView"
          @toggle-expanded-view="toggleExpandedView"
          :active-segment="activeSegment"
          :total-segments="parsedConversation.length"
          :segments="parsedConversation"
          :current-time="currentTime"
          :estimated-length="formattedLength"
          :voice-speed="voiceSpeed"
          :progress-percentage="progressPercentage"
          :is-downloading="isDownloading"
          :download-progress="downloadProgress"
          :download-ready="downloadReady"
          :is-muted="false"
          :showing-documents="showDocumentsInsteadOfSpeakers"
          :current-speaker="currentSpeaker"
          @toggle-play="togglePlay"
          @skip-backward="skipBackward"
          @skip-forward="skipForward"
          @seek="seek"
          @speed-change="onSpeedChange"
          @download="downloadFullConversation"
          @trigger-download="triggerDownload"
        />

        <citation-panel
          v-if="showCitationPanel && citationHistory.length > 0 && !showDocumentsInsteadOfSpeakers && !isPlaying"
          :citation-history="citationHistory"
          :search-results="searchResults"
          :segment-citation-urls="segmentCitationUrls"
        />

         <!-- YouTube Panel (when YouTube videos exist). -->
        <you-tube-panel
          v-if="showYouTubePanel && !showDocumentsInsteadOfSpeakers && !isPlaying"
          :search-results="searchResults"
          :is-active="showYouTubePanel"
          @video-started="handleYouTubeVideoStart"
          @video-closed="handleYouTubeVideoEnd"
        />

        <div v-if="hasYouTubeResults && !isPlaying" class="youtube-toggle">
          <button
            class="youtube-toggle-btn"
            :class="{ active: showYouTubePanel }"
            @click="toggleYouTubePanel"
            title="Toggle YouTube Videos">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
              <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
            </svg>
            <span class="youtube-count" v-if="youtubeVideos.length > 0">
              {{ youtubeVideos.length }}
            </span>
          </button>
        </div>
      </div>

      <!-- Right Column - Conversation Content -->
      <div class="conversation-column" :class="{ 'is-playing': isPlaying, 'is-expanded': isExpandedView }">
        <!-- Speaker Panel -->
        <speaker-panel
          :is-pro="isPro"
          :is-mobile="isMobile"
          :message-id="messageId"
          :speakers="uniqueSpeakers"
          :active-speaker="currentSpeaker"
          :is-playing="isPlaying"
          :is-expanded-view="isExpandedView"
          :always-show-speakers="true"
          :minimize-when-inactive="shouldMinimizeSpeaker"
          @customizations-loaded="onCustomizationsLoaded"
          @image-uploaded="onImageUploaded"
          @name-changed="onNameChanged"
          @role-changed="onRoleChanged"
          @voice-changed="onVoiceChanged"
          ref="speakerPanel"
        />
        <podcast-controls
          v-if="!isMobile"
          :is-mobile="isMobile"
          :is-pro="isPro"
          :is-playing="isPlaying"
          :is-expanded-view="isExpandedView"
          @toggle-expanded-view="toggleExpandedView"
          :active-segment="activeSegment"
          :total-segments="parsedConversation.length"
          :segments="parsedConversation"
          :current-time="currentTime"
          :estimated-length="formattedLength"
          :voice-speed="voiceSpeed"
          :progress-percentage="progressPercentage"
          :is-downloading="isDownloading"
          :download-progress="downloadProgress"
          :download-ready="downloadReady"
          :is-muted="false"
          :showing-documents="showDocumentsInsteadOfSpeakers"
          :current-speaker="currentSpeaker"
          @toggle-play="togglePlay"
          @skip-backward="skipBackward"
          @skip-forward="skipForward"
          @seek="seek"
          @speed-change="onSpeedChange"
          @download="downloadFullConversation"
          @trigger-download="triggerDownload"
        />
        <div v-if="parsedConversation.length > 0" class="conversation" ref="conversationContainer" :class="{ 'is-playing': isPlaying, 'is-expanded': isExpandedView }">
          <transition-group name="fade" mode="out-in">
            <div
              v-for="(segment, idx) in parsedConversation"
              :key="idx"
              class="segment"
              :class="{
                active: idx === activeSegment,
                'segment-host': segment.role && segment.role.toLowerCase().includes('host'),
                'segment-expert': segment.role && !segment.role.toLowerCase().includes('host'),
                'is-playing': isPlaying && idx === activeSegment,
                'is-expanded': isExpandedView,
                'has-custom-recording': customRecordings.has(idx)
              }"
              :ref="el => setSegmentRef(el, messageId, idx)"
            >
            <div class="speaker-info">
                <span class="speaker-name">{{ segment.speaker }}</span>
                <span class="segment-number">#{{ idx + 1 }}</span>
                <span class="speaker-role">({{ segment.role || 'Unknown' }})</span>

                <span v-if="customRecordings.has(idx)" class="custom-voice-badge" title="Custom voice recording">
                  🎤
                </span>

                <div
                  v-if="!isPlaying && idx === activeSegment"
                  class="modern-edit-indicator"
                  title="Click to edit content"
                  @click="focusContent(idx)"
                  :style="{ color: getWaveBarColor(idx), backgroundColor: `${getWaveBarColor(idx)}10` }"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z"/>
                  </svg>
                </div>

                <div v-if="isLoading && idx === activeSegment" class="loading-indicator">
                  <div class="loading-spinner" :style="{ 'border-top-color': getWaveBarColor(idx) }"></div>
                  <span class="loading-text" :style="{ color: getWaveBarColor(idx) }">Loading audio...</span>
                </div>

                <div v-if="isPlaying && idx === activeSegment" class="playing-indicator">
                  <div class="wave-bars">
                    <div class="wave-bar"
                        v-for="i in 3"
                        :key="i"
                        :style="{ '--wave-color': getWaveBarColor(idx) }"></div>
                  </div>
                  <span class="playing-text" :style="{ color: getWaveBarColor(idx) }">
                    {{ customRecordings.has(idx) ? 'Custom Recording' : 'Speaking' }}
                  </span>
                  </div>
                </div>
              <citation-renderer
                :html="segment.content"
                :isRagSourcesView="false"
                :chatId="chatId"
                :isSharedView="false"
                @citation-click="handleCitationClick"
                @citation-speaking="handleCitationSpeaking"
                :ref="`citationRenderer-${idx}`"
                @auto-scroll-complete="handleAutoScrollComplete"
                @full-audio-ready="handleFullAudioReady"
                @audio-progress="handleAudioProgress"
                @auto-scroll-progress="handleAutoScrollProgress"
              />

              <voice-recorder
                v-if="idx === activeSegment && !isPlaying"
                :is-pro="isPro"
                :segment-index="idx"
                :segment="segment"
                :message-id="messageId"
                :speaker-color="getWaveBarColor(idx)"
                @recording-saved="handleRecordingSaved"
                @recording-deleted="handleRecordingDeleted"
              />
            </div>
          </transition-group>
        </div>

        <!-- Loading/Error States -->
        <div v-else-if="error" class="error-state">
          {{ error }}
        </div>
        <div v-else class="loading-state">
          Loading conversation...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CitationRenderer from './CitationRenderer.vue';
import { voiceProfileMixin } from './mixins/voiceProfiles';
import { ttsPronunciationMixin } from './mixins/tts-pronunciation.mixin';
import VoiceProfileSelector from './VoiceProfileSelector.vue';
import SpeakerPanel from './SpeakerPanel.vue';
import SectionNavigator from './SectionNavigator.vue';
import DocumentSegmentMapper from './curated_content/DocumentSegmentMapper.vue';
import DocumentDisplay from './curated_content/DocumentDisplay.vue';
import VoiceRecorder from './curated_content/VoiceRecorder.vue';
import PodcastControls from './components/PodcastControls.vue';
import CitationPanel from './components/CitationPanel.vue';
import SegmentImagePanel from './components/SegmentImagePanel.vue';
import YouTubePanel from './components/YouTubePanel.vue';
import PlayButtonOverlay from './components/PlayButtonOverlay.vue';
import PostPlaybackOverlay from './components/PostPlaybackOverlay.vue';
import LiveTranscript from './components/LiveTranscript.vue';

import API from "@/api";

import { mapActions } from 'vuex';

export default {
  mixins: [voiceProfileMixin, ttsPronunciationMixin],
  name: 'ConversationPlayer',
    components: {
        CitationRenderer,
        VoiceProfileSelector,
        SpeakerPanel,
        SectionNavigator,
        DocumentSegmentMapper,
        DocumentDisplay,
        VoiceRecorder,
        PodcastControls,
        CitationPanel,
        SegmentImagePanel,
        YouTubePanel,
        PlayButtonOverlay,
        PostPlaybackOverlay,
        LiveTranscript
    },
  props: {
    voiceSettings: {
      type: Object,
      default: () => ({})
    },
    replayConversation: {
      type: Boolean,
      default: false
    },
    isSharedView: {
      type: Boolean,
      default: false
    },
    sharedId: {
      type: String,
      default: null
    },
    conversationTitle: {
      type: String,
      default: 'Conversation'
    },
    conversationDescription: {
      type: String,
      default: 'Listen to this AI-generated conversation'
    },
    conversationThumbnail: {
      type: String,
      default: null
    },
    isPro: {
      type: Boolean,
      default: false
    },
    lastDocumentUpdate: {
      type: Number,
      default: 0
    },
    documentData: {
      type: Object,
      default: () => ({})
    },
    allMessageIds: {
      type: Array,
      default: () => []
    },
    latestUserInput: {
      type: String,
      default: null
    },
    autoPlay: {
      type: Boolean,
      default: false
    },
    searchResults: {
      type: Array,
      default: () => []
    },
    isResponseInProgress: {
      type: Boolean,
      default: false
    },
    isLatestMessage: {
      type: Boolean,
      default: false
    },
    introFinished: {  // New prop to receive from IntroSpeakersPlayer.
      type: Boolean,
      default: false
    },
    messageId: {
      required: true
    },
    rawResponse: {
      type: String,
      required: true
    },
    chatId: {
      type: String,
      default: null
    },
  },
  emits: ['citation-click', 'parse-error'],
  data() {
    return {
        uploadedImages: {},
        allImages: [
          '/static/Gemini_Generated_Image_yznx90yznx90yznx.jpeg',
          '/static/Gemini_Generated_Image_3uyccg3uyccg3uyc.jpeg',
          '/static/Gemini_Generated_Image_r53bp9r53bp9r53b.jpeg',
          '/static/Gemini_Generated_Image_ns59fwns59fwns59.jpeg',
          '/static/Gemini_Generated_Image_yznx90yznx90yznx.jpeg',
          '/static/Gemini_Generated_Image_m68ngom68ngom68n.jpeg',
          '/static/Gemini_Generated_Image_qc0oqtqc0oqtqc0o.jpeg',
        ],
        hasStartedPlaying: false,
        customRecordingCache: new Map(),
        isExpandedView: true,
        showYouTubePanel: false,
        youtubeVideos: [],
        activeYouTubeVideo: null,
        isSegmentImageShowing: false,
        activeCitation: null,
        showCitationPanel: false,
        citationHistory: [],
        segmentCitationUrls: [],  // Store the citation URLs for the current segment
        citationIdToUrlMap: new Map(), // Map citation IDs to URLs
        isMobile: false,
        customRecordings: new Map(), // Will. store segmentIndex -> {audioBlob, audioUrl, duration}
        isUsingCustomRecording: false, // Flag to indicate if custom recording is being used
        documentImageDataMap: new Map(),  // New state to store documents with image data
        currentSegmentDocuments: [],
        showDocumentsInsteadOfSpeakers: false,  // New state to toggle between speakers and documents
        uploadedDocuments: {},
        speakerVoiceMap: new Map(),
        isLatestMessageLocal: this.isLatestMessage,
        introFinishedLocal: this.introFinished,
        uniqueSpeakers: [], // Will hold the list of unique speakers
        currentSpeaker: null, //
        isLoading: false,
        introAudio: null,
        downloadUrl: null,
        audioCache: new Map(), // Will store {voiceId, audioData} objects instead of just audioData
        isDownloading: false,
        downloadProgress: 0,
        downloadReady: false,
        currentSegmentBuffer: null,
        currentAudio: null,
        isPlaying: false,
        currentTime: 0,
        activeSegment: 0,
        audioContext: null,
        audioPlayer: null,
        speechSynth: null,
        utterance: null,
        parsedConversation: [],
        voices: [],
        voiceSpeed: this.voiceSettings.rate || 1.2,
        voiceProvider: 'local',
        selectedVoice: null,
        availableVoices: {
            google: [],
            edge: [],
            mobile: []
        },
        voiceQueue: [], // Will store available voices for the conversation
        voicePreferences: {
          edge: {
              host: ["Jenny", "Guy", "Tony"],
              expert: ["Christopher", "Eric", "Steven"],
              researcher: ["Clara", "Ana", "Aria"],
              analyst: ["Roger", "Davis", "Tony"],
              journalist: ["Guy", "Jenny", "Nancy"]
          },
          mobile: {
              host: ["Karen", "Samantha"],
              expert: ["Daniel", "Rishi"],
              researcher: ["Victoria", "Catherine"],
              analyst: ["Moira", "Tessa"],
              journalist: ["Daniel", "Rishi"]
              },
          chrome: {
              host: ["Google US English", "Chrome David"],
              expert: ["Google UK English Female", "Chrome Christopher"],
              researcher: ["Google UK English Male", "Chrome Catherine"],
              analyst: ["Google UK English Female", "Chrome George"],
              journalist: ["Google UK English Male", "Chrome James"]
          },
          local: {
            host: ["af+af_irulan+af_nicole+af_sky"],     // gentle and clear.
            expert: ["af_sky+am_adam+af_nicole"],
            analyst: ["af_sky+af_sarah+af"],
            third: ["bf_isabella+af_sky+af_nicole+af"],
            researcher: ["af_sky+am_adam+bf_isabella+af+af_bella"],
            professor: ["am_adam+bf_isabella+af_bella"],
          },
      },
        roleVoices: new Map(),
        timeInterval: null,
        error: null
    }
  },
  watch: {
    replayConversation(newVal) {
      if (newVal === true) {
        this.$nextTick(() => {
          this.play();
        });
      }
    },
    isPlaying(newVal) {
      this.$emit('podcast-playing', newVal, this.messageId);
      this.$nextTick(() => {
        if (newVal) {
          // If playing, make segments non-editable
          this.makeSegmentsNonEditable();
        } else {
          // If not playing, make segments editable
          this.makeSegmentsEditable();
        }
      });
    },
    searchResults: {
        handler(newResults) {
            // Process YouTube links in search results
            this.processYouTubeResults();
        },
        immediate: true
    },
    lastDocumentUpdate: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.processDocumentData(this.documentData);
          if (this.$refs.documentMapper) {
            this.$refs.documentMapper.refreshDocumentData();
          }
        }
      }
    },
    activeSegment: {
      handler(newVal) {
        this.$nextTick(() => {
          // Scroll to active segment
          this.scrollToActiveSegment(newVal);

          // Update the current segment documents
          this.currentSegmentDocuments = this.getCurrentSegmentDocuments();

          // Process new segment's citations
          this.processCitationsInSegment();

          // Display citations if available
          this.displaySegmentCitations();
        });
      },
      immediate: true
    },
    isResponseInProgress(newVal) {
      // when user enters a new message, pause the conversation.
      if (newVal === true) {
        // pause the intro music
        if (this.introAudio) {
          this.introAudio.pause();
        }
        // pause the conversation
        this.pause();
      }
    },
    isLatestMessage: {
      immediate: true, // This makes it run once on component creation
      handler(newVal) {
        this.isLatestMessageLocal = newVal;
      }
    },
    introFinished: {
      immediate: true,
      handler(newVal) {
        if (newVal === true && this.isLatestMessageLocal) {
          setTimeout(() => {
            this.play();
          }, 750);
        }
      }
    },
    latestUserInput(newVal) {
      if (newVal) {
        // pause the intro music
        if (this.introAudio) {
          this.introAudio.pause();
        }
      }
    },
    parsedConversation: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length) {
          this.extractUniqueSpeakers();
        }
      }
    },
    rawResponse: {
      immediate: true,
      handler(newVal) {
        try {
          // Clean up the HTML and extract JSON
          const cleanedResponse = this.cleanResponse(newVal);

          // Add additional validation before parsing
          if (!cleanedResponse.includes('{') || !cleanedResponse.includes('}')) {
            throw new Error('No valid JSON structure found in response');
          }

          const parsed = JSON.parse(cleanedResponse);

          if (parsed.conversation && Array.isArray(parsed.conversation)) {
            this.parsedConversation = parsed.conversation.map(segment => ({
              ...segment,
              content: this.cleanContent(segment.content)
            }));
          } else {
            throw new Error('Invalid conversation structure');
          }
        } catch (e) {
          console.error('Failed to parse conversation:', e);
          this.$emit('parse-error', e.message);
          this.parsedConversation = [{
            speaker: "System",
            role: "Error",
            content: `Failed to parse conversation: ${e.message}`,
            timestamp: 0
          }];
        }
      }
    }
},
  computed: {
    activeSegmentDisplayText() {
        if (!this.parsedConversation || this.activeSegment >= this.parsedConversation.length) {
            return '';
        }

        const content = this.parsedConversation[this.activeSegment].content;
        return this.getTextForSpeech(content);
    },
    hasYouTubeResults() {
        if (!this.searchResults || this.searchResults.length === 0) return false;
        return this.searchResults.some(result => this.isYouTubeUrl(result.url));
    },
    totalSegments() {
      return this.parsedConversation.length;
    },
    shouldMinimizeSpeaker() {
      // Minimize when there are supporting docs, segment images, or citations
      return this.isSegmentImageShowing ||
         this.showCitationPanel ||
         this.showDocumentsInsteadOfSpeakers
        // this.showYouTubePanel;
    },

    // Check if we should show a segment image
    shouldShowSegmentImage() {
      // Get all images from search results
      const images = this.searchResultImages;
      if (!images || images.length === 0) return false;

      // Define key segments where we want to show images
      const keySegments = [0, 4, 9, 14, 19, 24, 29];
      const fallbackSegments = [2, 6, 11, 16, 21];

      // Check if the current segment is in either key or fallback segments
      return [...keySegments, ...fallbackSegments].includes(this.activeSegment);
    },

    searchResultImages() {
      // Look for images in search results by examining URLs
      return this.searchResults.filter(result => {
        // Skip if no URL
        if (!result.url) return false;

        // Check for explicit image flag
        if (result.isImage) return true;

        // Check URL patterns typical of images
        const url = result.url.toLowerCase();

        // Common file extensions for images
        const imageExtensions = [
          '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp',
          '.ico', '.tiff', '.tif', '.img', '.heic', '.heif', '.raw', '.avif'
        ];

        // Check for file extensions
        const hasImageExtension = imageExtensions.some(ext => url.includes(ext));
        if (hasImageExtension) return true;

        // Check for common image patterns in URLs
        const imagePatterns = [
          'image', 'img', 'picture', 'photo', 'thumbnail', 'thumb',
          '/images/', '/pics/', '/photos/', '/avatars/', '/covers/',
          'media.licdn.com/dms/image', // LinkedIn images
          'profile-images', 'user-images',
          'fbcdn.net', // Facebook CDN
          'twimg.com', // Twitter images
          'instagram.com/p/', // Instagram posts
          'unsplash.com', // Unsplash
          'pexels.com', // Pexels
          'imgur.com', // Imgur
          'pinimg.com', // Pinterest
          'cloudfront.net', // AWS CloudFront (often used for images)
          'cloudinary.com', // Cloudinary image hosting
          'googleusercontent.com', // Google user content (often images)
          'gravatar.com' // Gravatars
        ];

        // Check for image service indicators in the URL
        return imagePatterns.some(pattern => url.includes(pattern));
      });
    },
    hasCustomRecording() {
      return this.customRecordings.has(this.activeSegment);
    },
    estimatedLength() {
      const baseWPM = 160;
      const adjustedWPM = baseWPM * this.voiceSpeed;

      const totalWords = this.parsedConversation.reduce((total, segment) => {
        const text = this.getTextForSpeech(segment.content);
        const words = text.trim().split(/\s+/).length;
        return total + words;
      }, 0);

      // Get total minutes
      const totalMinutes = totalWords / adjustedWPM;

      // Convert to seconds for more precise formatting
      return Math.round(totalMinutes * 60);
    },

    formattedLength() {
      const minutes = Math.floor(this.estimatedLength / 60);
      const seconds = Math.round(this.estimatedLength % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    buttonText() {
      if (this.isDownloading) {
        return `Preparing ${Math.round(this.progressPercentage)}%`
      }
      if (this.downloadReady) {
        return 'Download Ready'
      }
      return 'Download Conversation'
    },
    buttonIcon() {
      return {
        'is-downloading': this.isDownloading,
        'is-ready': this.downloadReady
      }
    },
    progressOffset() {
      const circumference = 2 * Math.PI * 10
      return circumference - (this.downloadProgress / 100) * circumference
    },
    progressPercentage() {
      return (this.activeSegment / (this.parsedConversation.length - 1)) * 100
    },
    isSafari() {
      const ua = navigator.userAgent.toLowerCase();
      return ua.includes('safari') && !ua.includes('chrome');
    },
    isFirefox() {
      return navigator.userAgent.toLowerCase().includes('firefox');
    },
    isEdge() {
      return navigator.userAgent.toLowerCase().includes('edg');
    },
  },
  created() {
    // Initialize audio context and player
    this.initAudioContext();
    this.initMediaSession();
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),
    jumpToSegment(index) {
      if (index >= 0 && index < this.parsedConversation.length) {
        this.activeSegment = index;
        this.scrollToActiveSegment(index);
      }
    },
    getActiveSpeakerImageUrl() {
      // Default image to use if anything fails
      const DEFAULT_IMAGE = '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg';

      try {
        // Safety check for undefined data
        if (!this.uniqueSpeakers || !Array.isArray(this.uniqueSpeakers)) {
          console.warn('uniqueSpeakers is not available yet');
          return DEFAULT_IMAGE;
        }

        // Safety check for active speaker
        if (!this.currentSpeaker) {
          return DEFAULT_IMAGE;
        }

        // Find current speaker index with safety check
        const speakerIndex = this.uniqueSpeakers.findIndex(
          speaker => speaker && speaker.name === this.currentSpeaker
        );

        // If no active speaker found, return default
        if (speakerIndex === -1) {
          console.warn('Speaker not found in uniqueSpeakers array');
          return DEFAULT_IMAGE;
        }

        // Create the unique key for this speaker's image
        const uniqueKey = `${this.messageId}_${speakerIndex}`;

        // If we have an uploaded image, use it
        if (this.uploadedImages && this.uploadedImages[uniqueKey]) {
          return this.uploadedImages[uniqueKey];
        }

        // Otherwise use a default image from the array
        if (this.allImages && this.allImages.length > 0) {
          return this.allImages[speakerIndex % this.allImages.length];
        }

        // Final fallback
        return DEFAULT_IMAGE;
      } catch (error) {
        console.error('Error getting speaker image:', error);
        return DEFAULT_IMAGE;
      }
    },
    handleOverlayPlay() {
      this.hasStartedPlaying = true;
      this.play();
    },
    forceRerender() {
      // Create a temporary copy
      const tempCopy = [...this.parsedConversation];

      // Replace the array with empty array and then with the copy
      // This forces Vue to completely re-render the component
      this.parsedConversation = [];

      this.$nextTick(() => {
        this.parsedConversation = tempCopy;

        // Update UI components that depend on current segments
        this.$nextTick(() => {
          this.extractUniqueSpeakers();
          this.currentSegmentDocuments = this.getCurrentSegmentDocuments();
          this.processCitationsInSegment();
          this.displaySegmentCitations();

          // Re-establish segment editability if not playing
          if (!this.isPlaying) {
            this.makeSegmentsEditable();
          }
        });
      });
    },
    async saveSegmentContent(index, newContent) {
      try {
        // Create a temporary div to strip HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newContent;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';

        // Create a proper dictionary/object as the server now expects
        const contentPayload = {
          content: plainText
        };

        const response = await API.post(
          `/user_customization/messages/${this.messageId}/segments/${index}/content`,
          contentPayload,  // Send the object/dictionary
          {
            headers: {
              'Content-Type': 'application/json'  // Changed to application/json
            }
          }
        );

        if (response.data.success) {
          console.log('Content updated successfully');
        }
      } catch (error) {
        console.error('Error updating segment content:', error);
      }
    },

    onCustomizationsLoaded(customData) {

      if (!customData || !customData.speakers || !customData.speakers.length) {
        console.log("No customizations to apply");
        return;
      }

      // Process each customization
      customData.speakers.forEach(customSpeaker => {
        // Skip if no custom name provided
        if (!customSpeaker.custom_name) return;

        // Apply to ALL segments with matching original name
        for (let i = 0; i < this.parsedConversation.length; i++) {
          if (this.parsedConversation[i].speaker === customSpeaker.original_name) {
            // Apply custom name
            this.parsedConversation[i].speaker = customSpeaker.custom_name;

            // Apply custom role if provided
            if (customSpeaker.custom_role) {
              this.parsedConversation[i].role = customSpeaker.custom_role;
            }
          }
        }

        // Update voice mapping if provided
        if (customSpeaker.voice_id) {
          this.speakerVoiceMap.set(customSpeaker.custom_name, customSpeaker.voice_id);
        }
      });

      // After applying all customizations, update related data
      this.extractUniqueSpeakers();

      // Update current speaker if needed
      if (this.activeSegment >= 0 && this.activeSegment < this.parsedConversation.length) {
        this.currentSpeaker = this.parsedConversation[this.activeSegment].speaker;
      }

      // Force UI update
      this.$forceUpdate();
    },
    async fetchSpeakerCustomizations() {
  try {
    // Get all customizations in a single request
    const token = localStorage.getItem("token");
    const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
    const url = token ? `/user_customization/messages/${this.messageId}/speaker-customizations` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/speaker-customizations`;
    const response = await API.get(url);
    const data = response.data;

    // Process speaker customizations
    if (data && data.speakers && data.speakers.length > 0) {

      // Apply customizations to ALL matching segments
      data.speakers.forEach(speaker => {
        if (speaker.custom_name) {
          // Update all segments with this original name
          for (let i = 0; i < this.parsedConversation.length; i++) {
            if (this.parsedConversation[i].speaker === speaker.original_name) {
              // Apply custom name
              this.parsedConversation[i].speaker = speaker.custom_name;

              // Apply custom role if provided
              if (speaker.custom_role) {
                this.parsedConversation[i].role = speaker.custom_role;
              }
            }
          }

          // Update current speaker if needed
          if (this.currentSpeaker === speaker.original_name) {
            this.currentSpeaker = speaker.custom_name;
          }

          // Update voice mappings
          if (speaker.voice_id) {
            this.speakerVoiceMap.set(speaker.custom_name, speaker.voice_id);
          }
        }
      });

      // Update uniqueSpeakers list based on the updated conversation
      this.extractUniqueSpeakers();

      // Emit event about loaded customizations
      this.$emit('speaker-customizations-loaded', data);

      // Force UI updates
      this.$nextTick(() => {
        // Update segment display
        this.scrollToActiveSegment(this.activeSegment);

        // Force UI components to update
        this.$forceUpdate();

        // Update child components if needed
        if (this.$refs.speakerPanel) {
          this.$refs.speakerPanel.$forceUpdate();
        }

        if (this.$refs.sectionNavigator) {
          this.$refs.sectionNavigator.$forceUpdate();
        }
      });
    }
  } catch (error) {
    console.error('Error fetching customizations:', error);
  }
},
    syncSpeakerData() {
      // Ensure uniqueSpeakers matches parsedConversation
      this.extractUniqueSpeakers();

      // Update current speaker references
      if (this.activeSegment >= 0 && this.activeSegment < this.parsedConversation.length) {
        this.currentSpeaker = this.parsedConversation[this.activeSegment].speaker;
      }

      // Force update speaker panel if it exists
      if (this.$refs.speakerPanel) {
        this.$refs.speakerPanel.$forceUpdate();
      }

      // Force update section navigator if it exists
      if (this.$refs.sectionNavigator) {
        this.$refs.sectionNavigator.$forceUpdate();
      }
    },
    async fetchVoiceRecordings() {
      try {
        // Get all voice recordings in a single request
        const token = localStorage.getItem("token");
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
        const url = token ? `/user_customization/messages/${this.messageId}/voice-recordings` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/voice-recordings`;
        const response = await API.get(url)
        const recordings = response.data;  // This is already the object with segment indices as keys

        // Initialize the customRecordings Map
        this.customRecordings = new Map();

        // Add each recording to the map
        Object.entries(recordings).forEach(([segmentIndex, recording]) => {
          // Use proxy URL instead of direct CloudFront URL
          const proxyUrl = token ? `/user_customization/messages/${this.messageId}/segments/${segmentIndex}/voice-proxy` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/segments/${segmentIndex}/voice-proxy`;

          this.customRecordings.set(parseInt(segmentIndex), {
            // Store both URLs - proxy for playback, original for reference
            audioUrl: proxyUrl,
            originalUrl: recording.url,
            duration: recording.duration
          });
        });

      } catch (error) {
        console.error('Error fetching voice recordings:', error);
      }
    },
    toggleExpandedView() {
      if (!this.isPro) {
        this.showTempMessageFn('Customizing conversation is only available to Pro users.', 'info');
        return;
      }
      this.isExpandedView = !this.isExpandedView;
      this.$emit('expanded-view-toggled', this.isExpandedView);
    },
    getWaveBarColor(segmentIndex) {
      // If we have a custom recording, use a different color (orange)
      if (this.customRecordings.has(segmentIndex)) {
        return '#ED8936';
      }

      // If there's a current speaker
      if (this.parsedConversation[segmentIndex]) {
        const speaker = this.parsedConversation[segmentIndex].speaker;
        const speakerIndex = this.uniqueSpeakers.findIndex(s => s.name === speaker);

        // Use the color from the speaker panel's color palette
        if (speakerIndex >= 0) {
          const colors = [
            '#1E88E5', // Blue
            '#8E24AA', // Purple
            '#43A047', // Green
            '#FB8C00', // Orange
            '#5E35B1', // Deep Purple
            '#00897B', // Teal
            '#F4511E', // Deep Orange
            '#7CB342'  // Light Green
          ];

          return colors[speakerIndex % colors.length];
        }
      }

      // Default color
      return '#4299e1';
    },
    isYouTubeUrl(url) {
        if (!url) return false;
        return url.includes('youtube.com') ||
               url.includes('youtu.be') ||
               url.includes('youtube-nocookie.com');
    },
    // Handle when a user clicks to play a YouTube video
    handleYouTubeVideoStart(video) {
        this.activeYouTubeVideo = video;

        // Pause any playing audio to avoid conflicts
        if (this.isPlaying) {
            this.pause();
        }

        // Hide other panels to give focus to the video
        this.showCitationPanel = false;
        this.showDocumentsInsteadOfSpeakers = false;
        this.isSegmentImageShowing = false;

        // Emit event for parent components
        this.$emit('youtube-video-started', video);
    },

    // Handle when a video is closed
    handleYouTubeVideoEnd() {
        this.activeYouTubeVideo = null;

        // Emit event for parent components
        this.$emit('youtube-video-ended');
    },

    // Toggle YouTube panel visibility
    toggleYouTubePanel() {
        this.showYouTubePanel = !this.showYouTubePanel;

        // If showing YouTube panel, hide other panels
        if (this.showYouTubePanel) {
            this.showCitationPanel = false;
            this.showDocumentsInsteadOfSpeakers = false;
        }
    },

    // Process search results for YouTube videos
    processYouTubeResults() {
        if (!this.searchResults || this.searchResults.length === 0) {
            this.youtubeVideos = [];
            return;
        }
        this.youtubeVideos = this.searchResults.filter(result =>
            this.isYouTubeUrl(result.url)
        );

        // Auto-show YouTube panel if videos are present and no other panel is active
        if (this.youtubeVideos.length > 0 &&
            !this.showCitationPanel &&
            !this.showDocumentsInsteadOfSpeakers &&
            !this.isSegmentImageShowing) {
            // this.showYouTubePanel = true;
        }
    },
    onImageVisibilityChanged(isVisible, image) {
      // Update our local state
      this.isSegmentImageShowing = isVisible;

      // Optional: You can also emit an event up to parent components if needed
      this.$emit('segment-image-visibility-changed', isVisible, image);

      // Optional: Trigger any additional UI updates needed
      this.$nextTick(() => {
        // Any additional UI updates that depend on image visibility
      });
    },

    // You can also add a method to programmatically check image visibility
    checkImageVisibility(segmentIndex) {
      if (this.$refs.segmentImagePanel) {
        return this.$refs.segmentImagePanel.isImageShownForSegment(segmentIndex);
      }
      return false;
    },
    extractSourceTextForCitation(citationId) {
      if (this.activeSegment === null || this.activeSegment >= this.parsedConversation.length) {
        return null;
      }

      const content = this.parsedConversation[this.activeSegment].content;
      if (!content) return null;

      // Create a temporary div to parse the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      // Find the citation link with matching ID
      const citationLink = tempDiv.querySelector(`.citation-link[data-citation="${citationId}"]`);
      if (!citationLink) return null;

      // Get the source text from data attribute
      let sourceText = citationLink.dataset.sourceText || '';

      // Check if we need to decode the source text
      try {
        sourceText = decodeURIComponent(sourceText);
      } catch (e) {
        // If decoding fails, use as is
        console.warn(`Failed to decode source text for citation ${citationId}:`, e);
      }

      // Clean up the source text
      sourceText = this.cleanSourceText(sourceText);

      return sourceText;
    },
    cleanSourceText(text) {
      if (!text) return '';

      return text
        // Remove URL patterns
        .replace(/\[URL:\s*(https?:\/\/[^\]]+)\]/g, '')
        .replace(/\[Source:\s*(https?:\/\/[^\]]+)\]/g, '')

        // Remove HTML tags
        .replace(/<[^>]+>/g, '')

        // Decode HTML entities
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&nbsp;/g, ' ')

        // Normalize whitespace
        .replace(/\s+/g, ' ')
        .trim();
    },

    // Extract metadata from citation
    extractMetadataForCitation(citationId) {
      if (this.activeSegment === null || this.activeSegment >= this.parsedConversation.length) {
        return {};
      }

      const content = this.parsedConversation[this.activeSegment].content;
      if (!content) return {};

      // Create a temporary div to parse the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      // Find the citation link with matching ID
      const citationLink = tempDiv.querySelector(`.citation-link[data-citation="${citationId}"]`);
      if (!citationLink) return {};

      // Get the metadata from data attribute
      const metadataStr = citationLink.dataset.metadata;
      if (!metadataStr) return {};

      // Parse the metadata JSON
      try {
        return JSON.parse(metadataStr);
      } catch (e) {
        console.warn(`Failed to parse metadata for citation ${citationId}:`, e);
        return {};
      }
    },

    processCitationsInSegment() {
      // Skip if no active segment
      if (this.activeSegment === null || this.activeSegment >= this.parsedConversation.length) {
        return;
      }

      // Get the content of active segment
      const content = this.parsedConversation[this.activeSegment].content;
      if (!content) return;

      // Try to get the citation renderer for this segment
      const rendererRef = this.$refs[`citationRenderer-${this.activeSegment}`];
      if (!rendererRef) return;

      // Get renderer instance (might be array if v-for is used)
      const renderer = Array.isArray(rendererRef) ? rendererRef[0] : rendererRef;
      if (!renderer || !renderer.citationUrls) return;

      // Get citation URLs map from the renderer
      const citationUrlsMap = renderer.citationUrls;
      if (!citationUrlsMap.size) return;

      // Store citation URLs for this segment
      // Process the keys to extract the citation IDs from the composite keys
      this.segmentCitationUrls = Array.from(citationUrlsMap.entries()).map(([compositeKey, url]) => {
        // compositeKey format is "${chatId}-${citationId}"
        const parts = compositeKey.split('-');
        const citationId = parts.length > 1 ? parts[parts.length - 1] : compositeKey;

        // Store in our direct mapping
        this.citationIdToUrlMap.set(citationId, url);

        return [citationId, url];
      });

      // Emit the citation URLs to parent
      this.$emit('segment-citations', {
        segmentIndex: this.activeSegment,
        citationUrls: this.segmentCitationUrls
      });
    },

    getCitationUrl(citationId) {
      // First check our direct mapping
      if (this.citationIdToUrlMap.has(citationId)) {
        return this.citationIdToUrlMap.get(citationId);
      }

      // Fallback: Try to find in the current segment's citation renderer
      const rendererRef = this.$refs[`citationRenderer-${this.activeSegment}`];
      if (!rendererRef) return null;

      const renderer = Array.isArray(rendererRef) ? rendererRef[0] : rendererRef;
      if (!renderer || !renderer.citationUrls) return null;

      // Try with both formats: just ID and composite key
      const compositeKey = `${this.chatId}-${citationId}`;
      if (renderer.citationUrls.has(compositeKey)) {
        const url = renderer.citationUrls.get(compositeKey);
        // Cache it in our map for future use
        this.citationIdToUrlMap.set(citationId, url);
        return url;
      }

      return null;
    },

    findCitationsInSegment() {
      if (this.activeSegment === null || this.activeSegment >= this.parsedConversation.length) {
        return [];
      }

      const content = this.parsedConversation[this.activeSegment].content;
      if (!content) return [];

      // Create a temporary div to parse the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      // Find all citation links
      const citationLinks = Array.from(tempDiv.querySelectorAll('.citation-link'));

      // Extract citation IDs and source text
      return citationLinks.map(link => {
        const citationId = link.dataset.citation;
        const sourceText = link.dataset.sourceText || '';

        // Try to get URL from our mapping
        const url = this.getCitationUrl(citationId);

        return {
          id: citationId,
          url: url,
          sourceText: sourceText
        };
      });
    },

    displaySegmentCitations() {
      // Reset citation display state
      this.citationHistory = [];

      // Get citations from segment
      const citations = this.findCitationsInSegment();

      // If we have citations, populate the history and show the panel
      if (citations.length > 0) {
        this.citationHistory = citations;
        this.showCitationPanel = true;
      } else {
        this.showCitationPanel = false;
      }
    },

    onSpeedChange(speed) {
      this.voiceSpeed = speed;
      // clear the audio cache and reload the conversation
      this.clearAudioCacheAndReload();
    },
    // Replace the getCurrentSegmentDocuments method in ConversationPlayer.vue
    handleRecordingSaved(recordingData) {
    // Store the recording data
    this.customRecordings.set(recordingData.segmentIndex, {
      audioBlob: recordingData.audioBlob,
      audioUrl: recordingData.audioUrl,
      duration: recordingData.duration
    });

    // Clear any TTS audio cache for this segment to ensure we use the recording
    if (this.audioCache.has(recordingData.segmentIndex)) {
      // Only delete this specific segment from cache
      this.audioCache.delete(recordingData.segmentIndex);
    }

    // Show success message
    this.showTempMessageFn({
      message: "Voice recording saved successfully!",
      type: "success",
      timeout: 3000
    });

    // If currently playing and at this segment, restart with the new recording
    if (this.isPlaying && this.activeSegment === recordingData.segmentIndex) {
      this.pause();
      this.play();
    }
  },

  // Method to handle deleting a recording
  handleRecordingDeleted(segmentIndex) {
    if (this.customRecordings.has(segmentIndex)) {
      // Get the recording data
      const recordingData = this.customRecordings.get(segmentIndex);

      // Revoke object URL to prevent memory leaks
      if (recordingData.audioUrl) {
        URL.revokeObjectURL(recordingData.audioUrl);
      }

      // Remove from the map
      this.customRecordings.delete(segmentIndex);

      // Show message
      this.showTempMessageFn({
        message: "Voice recording removed",
        type: "info",
        timeout: 2000
      });

      // If currently playing and at this segment, restart with TTS
      if (this.isPlaying && this.activeSegment === segmentIndex) {
        this.pause();
        this.play();
      }
    }
  },
    processDocumentData(newDocumentData) {
      // Initialize if needed
      if (!this.uploadedDocuments) {
        this.uploadedDocuments = {};
      }

      // We need to structure the uploadedDocuments object correctly
      // to maintain compatibility with existing code
      if (newDocumentData) {
        Object.keys(newDocumentData).forEach(messageId => {
          // Initialize messageId object if needed
          if (!this.uploadedDocuments[messageId]) {
            this.uploadedDocuments[messageId] = {};
          }

          Object.keys(newDocumentData[messageId]).forEach(docKey => {
            const doc = newDocumentData[messageId][docKey];

            this.uploadedDocuments[messageId][docKey] = {
              id: doc.id,
              name: doc.name,
              type: doc.type,
              size: doc.size,
              timestamp: doc.timestamp || Date.now(),
              dataUrl: doc.preview || doc.dataUrl,
              uniqueKey: docKey
            };
          });
        });
      }

      this.currentSegmentDocuments = this.getCurrentSegmentDocuments();

    },
    getCurrentSegmentDocuments() {
      if (!this.$refs.documentMapper) return [];

      try {
        // Get document keys for current segment
        const segmentMap = this.$refs.documentMapper.documentSegmentMap || {};
        const docKeys = segmentMap[this.activeSegment] || [];

        if (docKeys.length === 0) {
          this.showDocumentsInsteadOfSpeakers = false;
          return [];
        }

        // Build documents array directly from documentData prop
        const documents = [];

        docKeys.forEach(docKey => {
          // Extract message ID from document key
          const [messageId] = docKey.split('_');

          // First try to get document from documentData prop
          if (this.documentData &&
              this.documentData[messageId] &&
              this.documentData[messageId][docKey]) {
            // Use the document data from props
            documents.push(this.documentData[messageId][docKey]);
          }
        });

        this.showDocumentsInsteadOfSpeakers = documents.length > 0;
        return documents;
      } catch (error) {
        console.error('Error getting documents for segment:', error);
        return [];
      }
    },

    async fetchDocumentData() {
      try {
        // Get documents from backend API
        const token = localStorage.getItem("token");
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
        const url = token ? `/user_customization/messages/${this.messageId}/documents` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/documents`;
        const response = await API.get(url);
        this.uploadedDocuments = response.data;

        // Get segment mappings
        const mappingUrl = token ? `/user_customization/messages/${this.messageId}/segments/mappings` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/segments/mappings`;
        const mappingResponse = await API.get(mappingUrl);

        // Update document mapper component if it exists
        if (this.$refs.documentMapper) {
          this.$refs.documentMapper.documentSegmentMap = mappingResponse.data;
        }

        // Update current segment documents
        this.currentSegmentDocuments = this.getCurrentSegmentDocuments();

      } catch (error) {
        console.error('Error fetching document data:', error);
      }
    },
    debugDocumentStorage() {
      console.group('Document Storage Debug');

      // 1. List all localStorage keys related to documents
      console.log('LocalStorage keys:');
      const docKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.includes('upload') ||
            key.includes('image') ||
            key.includes('thumbnail') ||
            key.includes('doc-segment')) {
          docKeys.push(key);
        }
      }
      console.table(docKeys);

      // 2. Check segment mappings
      if (this.$refs.documentMapper) {
        console.log('Document-segment mappings:', this.$refs.documentMapper.documentSegmentMap);
      }

      // 3. Check current segment documents
      console.log('Current segment documents:', this.currentSegmentDocuments);

      // 4. Check uploadedDocuments object
      console.log('Uploaded documents object:', this.uploadedDocuments);

      console.groupEnd();
    },

    handleMappingUpdated(mappingData) {

      // If the active segment is the one that was just updated, refresh the display
      if (this.activeSegment === mappingData.segmentIndex) {
        // Update the current segment documents
        this.currentSegmentDocuments = this.getCurrentSegmentDocuments();
      }
    },

    // Override activeSegment watcher or handleSegmentEnd to show mapped documents
    handleActiveSegmentChange(newSegmentIndex) {
      // First do any existing behavior for segment changes
      this.scrollToActiveSegment(newSegmentIndex);

      // Then update the current segment documents
      this.currentSegmentDocuments = this.getCurrentSegmentDocuments();
    },

    // Helper functions
    getAllMessageIds() {
      // Implementation depends on your chat data structure
      // This could come from your Vuex store, props, or component data

      // Example:
      return this.$store.getters['chat/getAllMessageIds'](this.chatId);

      // Or if messages are stored locally:
      // return this.messages.map(msg => msg.id);
    },

    // Helper method to get document thumbnail
    getDocumentThumbnail(messageId, documentId) {
      // Implementation depends on how you store document previews
      // This could be a URL to a server-stored thumbnail or a data URL

      // Example for data URL stored in localStorage:
      const thumbnailKey = `thumbnail-${messageId}-${documentId}`;
      return localStorage.getItem(thumbnailKey);

      // Or if you have a server endpoint:
      // return `${this.apiBaseUrl}/thumbnails/${messageId}/${documentId}`;
    },

    // Add method to be called when segment contains documents
    showSegmentDocuments(segmentIndex) {
      // You may want to show a notification or indicator that documents are available
      this.showTempMessageFn({
        message: "Supporting documents available for this segment",
        type: "info",
        timeout: 2000
      });
    },

    // Add this method to your component methods
    focusContent(index) {
      // Check if user is a pro user
      if (!this.isPro) {
        this.showTempMessageFn({
          message: "Customizing conversation is only available to Pro users.",
          type: "info",
        });
        return;
      }
      this.$nextTick(() => {
        // Find the segment using the correct selector based on data-message-id
        let segment;

        // First try with data-message-id
        const messageContainer = document.querySelector(`[data-message-id="${this.messageId}"]`);
        if (messageContainer) {
          const segments = messageContainer.querySelectorAll('.segment');
          segment = segments[index];
        }

        // Fallback to component scope if not found
        if (!segment) {
          const segmentsInComponent = this.$el.querySelectorAll('.segment');
          segment = segmentsInComponent[index];
        }

        if (segment) {
          const renderer = segment.querySelector('.citation-renderer');
          if (renderer) {
            // Focus the content and place cursor at the end
            renderer.focus();

            // Place cursor at the end of the content
            const range = document.createRange();
            const selection = window.getSelection();

            // Try to place cursor at end
            try {
              range.selectNodeContents(renderer);
              range.collapse(false); // false means collapse to end
              selection.removeAllRanges();
              selection.addRange(range);
            } catch (e) {
              console.warn('Could not set cursor position:', e);
            }

            // Add visual indicator that we're in edit mode
            renderer.classList.add('actively-editing');
          }
        }
      });
    },
    makeSegmentsNonEditable() {
      this.$nextTick(() => {
        // First try to find segments with data-message-id attribute
        const segments = document.querySelectorAll(`[data-message-id="${this.messageId}"] .segment`);

        // If no segments found with data-message-id, fallback to segments within this component
        const segmentsToProcess = segments.length > 0 ? segments : this.$el.querySelectorAll('.segment');

        segmentsToProcess.forEach((segment) => {
          const renderer = segment.querySelector('.citation-renderer');

          if (renderer) {
            // Remove contenteditable attribute
            renderer.removeAttribute('contenteditable');

            // Remove character counter if exists
            const counter = segment.querySelector('.character-counter');
            if (counter) {
              counter.remove();
            }
          }
        });
      });
    },
    makeSegmentsEditable() {
      // do not make it editable if not pro
      if (!this.isPro) {
        return;
      }
      this.$nextTick(() => {
        // First try to find segments with data-message-id attribute
        const segments = document.querySelectorAll(`[data-message-id="${this.messageId}"] .segment`);

        // If no segments found with data-message-id, fallback to segments within this component
        if (segments.length === 0) {
          // Try to find segments within this component's root element
          const segmentsInComponent = this.$el.querySelectorAll('.segment');

          segmentsInComponent.forEach((segment, index) => {
            if (index < this.parsedConversation.length) {
              makeEditableAndAddListener(segment, index);
            }
          });
        } else {
          segments.forEach((segment, index) => {
            if (index < this.parsedConversation.length) {
              makeEditableAndAddListener(segment, index);
            }
          });
        }
      });

      // Helper function to set up editable behavior
      const makeEditableAndAddListener = (segment, index) => {
        const renderer = segment.querySelector('.citation-renderer');

        if (renderer) {
          // Make the renderer editable
          renderer.setAttribute('contenteditable', 'true');

          // Add paste event listener to force plain text with token limit
          renderer.addEventListener('paste', (e) => {
            // Prevent the default paste
            e.preventDefault();

            // Get plain text from clipboard
            let text = (e.clipboardData || window.clipboardData).getData('text/plain');

            // Calculate approximate token count (rough estimate: 4 chars ≈ 1 token)
            const MAX_TOKENS = 510;
            const CHARS_PER_TOKEN = 4;
            const MAX_CHARS = MAX_TOKENS * CHARS_PER_TOKEN; // About 2040 characters

            // Get existing content length
            const existingText = renderer.textContent || '';
            const existingChars = existingText.length;

            // Calculate how many more characters we can add
            const availableChars = MAX_CHARS - existingChars;

            // Check if pasted text exceeds available space
            if (text.length > availableChars) {
              // Truncate the text
              text = text.substring(0, availableChars);

              // Show warning to user
              this.$nextTick(() => {
                this.showTempMessageFn({
                  message: "Text was truncated to stay within the token limit for TTS processing",
                  type: "warning",
                  timeout: 5000
                });
              });
            }

            // Insert the text at cursor position
            document.execCommand('insertText', false, text);
          });

          // Add input event listener to check content length in real-time
          renderer.addEventListener('input', (e) => {
            const MAX_TOKENS = 510;
            const CHARS_PER_TOKEN = 4;
            const MAX_CHARS = MAX_TOKENS * CHARS_PER_TOKEN;

            const text = renderer.textContent || '';

            if (text.length > MAX_CHARS) {
              // Truncate the text
              renderer.textContent = text.substring(0, MAX_CHARS);

              // Position cursor at the end
              const range = document.createRange();
              const selection = window.getSelection();
              range.selectNodeContents(renderer);
              range.collapse(false);
              selection.removeAllRanges();
              selection.addRange(range);

              // Show warning
              this.showTempMessageFn({
                message: "Maximum text length reached for TTS processing",
                type: "warning",
                timeout: 3000
              });
            }
          });

          // Add blur event to save content when user clicks away
          renderer.addEventListener('blur', () => {
            this.updateSegmentContent(index, renderer.innerHTML);
          });

          // Add a visual character counter
          const counter = document.createElement('div');
          counter.className = 'character-counter';
          counter.style.fontSize = '11px';
          counter.style.color = '#666';
          counter.style.marginTop = '4px';
          counter.style.textAlign = 'right';

          // Update counter function
          const updateCounter = () => {
            const text = renderer.textContent || '';
            const MAX_TOKENS = 510;
            const CHARS_PER_TOKEN = 4;
            const MAX_CHARS = MAX_TOKENS * CHARS_PER_TOKEN;

            const tokenCount = Math.ceil(text.length / CHARS_PER_TOKEN);
            const percentage = Math.round((text.length / MAX_CHARS) * 100);

            counter.textContent = `${text.length} chars / ~${tokenCount} tokens (${percentage}%)`;

            // Change color as it gets closer to limit
            if (percentage > 90) {
              counter.style.color = '#e53e3e'; // red
            } else if (percentage > 75) {
              counter.style.color = '#dd6b20'; // orange
            } else {
              counter.style.color = '#666'; // default gray
            }
          };

          // Initial counter update
          updateCounter();

          // Update counter on input
          renderer.addEventListener('input', updateCounter);
        }
      };
    },

    // Simple update method - no need for complex cleaning
    updateSegmentContent(index, newContent) {

      // Update the segment in a reactive way
      this.$set(this.parsedConversation, index, {
        ...this.parsedConversation[index],
        content: newContent
      });

      // Clear audio cache to regenerate TTS
      this.clearAudioCache();

      // If currently playing this segment, restart it only if it's the active segment and if the content changed
      if (this.isPlaying && this.activeSegment === index) {
        this.pause();
        this.play();
      }

      // Save the updated content to the server
      this.saveSegmentContent(index, newContent);
    },
   onVoiceChanged(data) {

      // Create a map of speaker names to voice IDs if it doesn't exist
      if (!this.speakerVoiceMap) {
        this.speakerVoiceMap = new Map();
      }

      // Update the voice mapping
      this.speakerVoiceMap.set(data.speakerName, data.voiceId);

      // Clear audio cache to regenerate with new voice
      this.clearAudioCache();

      // If currently playing, restart with new voice
      if (this.isPlaying) {
        const currentSegment = this.activeSegment;
        this.pause();
        this.activeSegment = currentSegment;
        this.play(); // play() will trigger prefetching of next segment via startPodcast()
      } else {
        // CRITICAL FIX: Even if not playing, prefetch the current AND next segments
        // This ensures prefetching continues after a voice change
        this.getAudioForSegment(this.activeSegment)
          .then(() => {
            // After current segment is fetched, prefetch the next one
            const nextSegmentIndex = this.activeSegment + 1;
            if (nextSegmentIndex < this.parsedConversation.length) {
              return this.getAudioForSegment(nextSegmentIndex);
            }
          })
          .catch(error => {
            console.error('Failed to prefetch segments after voice change:', error);
          });
      }
    },

    // Override the mixin's getVoiceForSpeaker method
    // This should be placed after the mixin is imported in your component
    // so it overrides the mixin's method

    // In your methods object:
   // FIXED getVoiceForSpeaker - pass the actual speaker's name
    getVoiceForSpeaker(speaker) {
      // Check if we have a specific voice mapping for this speaker
      if (speaker && this.speakerVoiceMap && this.speakerVoiceMap.has(speaker)) {
        // If we have a specific voice mapping for this speaker, use it
        return this.speakerVoiceMap.get(speaker);
      }

      // If no specific mapping, use the mixin's implementation
      // which will handle profile-based voice selection
      return this.$options.mixins
        .find(mixin => mixin.methods && mixin.methods.getVoiceForSpeaker)
        .methods.getVoiceForSpeaker.call(this, speaker);
    },

    onRoleChanged(data) {
      // Make a deep copy of uniqueSpeakers to avoid mutation issues
      const speakersCopy = JSON.parse(JSON.stringify(this.uniqueSpeakers));

      // Update the speaker role in the uniqueSpeakers array
      speakersCopy[data.index].role = data.newRole;
      this.uniqueSpeakers = speakersCopy;

      // Also update in parsedConversation to maintain consistency
      // This ensures all segments with the old role get updated
      this.parsedConversation = this.parsedConversation.map(segment => {
        if (segment.speaker === data.speakerName) {
          return {
            ...segment,
            role: data.newRole
          };
        }
        return segment;
      });
    },

   // In ConversationPlayer.vue, replace the existing onNameChanged method with this:
    onNameChanged(data) {
      // Make a deep copy of uniqueSpeakers to avoid mutation issues
      const speakersCopy = JSON.parse(JSON.stringify(this.uniqueSpeakers));

      // Update the speaker name in the uniqueSpeakers array
      speakersCopy[data.index].name = data.newName;
      this.uniqueSpeakers = speakersCopy;

      // Update parsedConversation by creating a new array with updated objects
      this.parsedConversation = this.parsedConversation.map(segment => {
        if (segment.speaker === data.oldName) {
          // Create a new object with the updated speaker name
          return {
            ...segment,
            speaker: data.newName
          };
        }
        return segment;
      });

      // Update the current speaker reference if it's the one being changed
      if (this.currentSpeaker === data.oldName) {
        this.currentSpeaker = data.newName;
      }

      // Update the voice mapping when the speaker name changes
      if (this.speakerVoiceMap && this.speakerVoiceMap.has(data.oldName)) {
        // Get the voice ID associated with the old name
        const voiceId = this.speakerVoiceMap.get(data.oldName);

        // Delete the old mapping
        this.speakerVoiceMap.delete(data.oldName);

        // Create a new mapping with the new name
        this.speakerVoiceMap.set(data.newName, voiceId);
      }

      // Force re-render of all segments
      this.$nextTick(() => {
        // This ensures Vue updates the DOM after the data changes
        this.makeSegmentsNonEditable();
        this.makeSegmentsEditable();
        this.forceRerender();
      });

      // Clear audio cache since speaker names are changed
      this.clearAudioCache();
    },
    // Add this to mounted or created hook
    fetchSpeakerImages() {
      try {
        const token = localStorage.getItem("token");
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
        const url = token
          ? `/user_customization/messages/${this.messageId}/customize-data`
          : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/customize-data`;

        API.get(url).then(response => {
          const data = response.data;

          // Load speaker images
          if (data.speakerImages) {
            Object.keys(data.speakerImages).forEach(index => {
              const img = data.speakerImages[index];
              const uniqueKey = `${this.messageId}_${index}`;
              this.$set(this.uploadedImages, uniqueKey, img.url);
            });
          }
        });
      } catch (error) {
        console.error('Error fetching speaker images:', error);
      }
    },
    onImageUploaded(data) {
      // Create a unique key using messageId and index
      const uniqueKey = `${data.messageId}_${data.index}`;

      // Store the image URL
      if (!this.uploadedImages) {
        this.uploadedImages = {};
      }

      // Use $set for reactivity
      this.$set(this.uploadedImages, uniqueKey, data.imageUrl);

      // Force UI update
      this.$forceUpdate();
    },

    jumpToSection(segmentIndex) {
      const wasPlaying = this.isPlaying;
      if (wasPlaying) {
        this.pause();
      }
      this.activeSegment = segmentIndex;
      this.$nextTick(() => {
        this.scrollToActiveSegment(segmentIndex);
        if (wasPlaying) {
          this.play();
        }
      });
    },
    extractUniqueSpeakers() {
      // Use a Map to maintain speaker uniqueness by name
      const speakerMap = new Map();

      this.parsedConversation.forEach(segment => {
        if (!speakerMap.has(segment.speaker)) {
          speakerMap.set(segment.speaker, {
            name: segment.speaker,
            role: segment.role
          });
        }
      });

      // Convert Map values to array
      this.uniqueSpeakers = Array.from(speakerMap.values());
    },
    playIntroMusic(volume=0.05, audioName="Odyssey-Eveningland.mp3") {
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();

      // Create gain node for volume
      const gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);
      gainNode.gain.value = volume;

      // Create and play audio
      this.introAudio = new Audio(`https://${window.location.host}/static/${audioName}`);
      const source = audioContext.createMediaElementSource(this.introAudio);
      source.connect(gainNode);

      // for mobile add a lock/unlock audio context event listener
      if (this.isMobile) {
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this.introAudio.pause();
          }
        });
      }

      this.introAudio.play();
    },
    computeProgressOffset(percentage) {
      const circumference = 2 * Math.PI * 10
      return circumference - (percentage / 100) * circumference
    },
    setSegmentRef(el, messageId, index) {
        // Create the ref key
        const refKey = `segment-${messageId}-${index}`;

        // Ensure this.$refs exists
        if (!this.$refs[refKey]) {
            this.$set(this.$refs, refKey, el);
        } else {
            this.$refs[refKey] = el;
        }
    },

    scrollToActiveSegment(index) {
        const conversationContainer = this.$refs.conversationContainer;
        const segmentRef = this.$refs[`segment-${this.messageId}-${index}`];

        if (!conversationContainer) {
            console.warn('Conversation container not found');
            return;
        }

        if (!segmentRef) {
            console.warn('Active segment not found');
            return;
        }

        const containerRect = conversationContainer.getBoundingClientRect();
        const segmentRect = segmentRef.getBoundingClientRect();

        const scrollTop = segmentRect.top - containerRect.top + conversationContainer.scrollTop - (containerRect.height / 2) + (segmentRect.height/2);

        conversationContainer.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
        });
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
      if (this.isMobile) {
        this.showYouTubePanel = false
      }
    },

    getPlatformType() {
      if (this.isEdge && !this.isMobile) return 'edge';
      if (this.isMobile || this.isSafari || this.isFirefox) return 'mobile';
      return 'chrome';
    },

    normalizeRole(role) {
     return role
    },
    handleCitationClick(citationData) {
      // Pass the complete citation data up
      this.$emit('citation-click', {
        citation: citationData.citation,
        element: citationData.element,
        sourceText: citationData.sourceText,
        metadata: citationData.metadata
      });
    },
    handleCitationSpeaking(data) {
      this.$emit('citation-speaking', data);
    },

    handleAutoScrollComplete() {
      this.$emit('auto-scroll-complete');
    },
    handleFullAudioReady() {
      this.$emit('full-audio-ready');
    },
    handleAudioProgress(progress) {
      this.$emit('audio-progress', progress);
    },
    handleAutoScrollProgress(progress) {
      this.$emit('auto-scroll-progress', progress);
    },
    // Enhanced cleaning methods
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    cleanResponse(response) {
    if (!response) return '';

    try {
      // Step 1: Initial HTML entity decoding
      let cleaned = this.decodeHtmlEntities(response);

      // Step 2: Handle citation spans carefully
      cleaned = this.processCitationSpans(cleaned);

      // Step 3: Clean HTML and structure
      cleaned = this.cleanHtmlAndStructure(cleaned);

      // Step 4: Advanced JSON repair
      cleaned = this.repairJson(cleaned);

      return cleaned;
    } catch (e) {
      console.error('Error in cleanResponse:', e);
      return this.getFallbackResponse();
    }
  },

  decodeHtmlEntities(text) {
    const entities = {
      '&quot;': '"',
      '&#x27;': "'",
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>'
    };

    let decoded = text;
    // Replace named entities
    Object.entries(entities).forEach(([entity, char]) => {
      decoded = decoded.replace(new RegExp(entity, 'g'), char);
    });

    // Replace decimal entities
    decoded = decoded.replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(dec));

    // Replace hex entities
    decoded = decoded.replace(/&#x([0-9a-f]+);/gi, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    );

    return decoded;
  },

  processCitationSpans(text) {
    return text.replace(
      /<span[^>]*?data-source-text="([^"]*?)".*?>(.*?)<\/span>/g,
      (match) => {
        return match
          .replace(/\\/g, '\\\\')
          .replace(/"/g, '\\"')
          .replace(/[\n\r\t]/g, (c) => ({
            '\n': '\\n',
            '\r': '\\r',
            '\t': '\\t'
          }[c]));
      }
    );
  },

  cleanHtmlAndStructure(text) {
    // Remove all HTML tags except spans
    let cleaned = text.replace(/<(?!\/?span)[^>]+>/g, '');

    return cleaned;
  },

  repairJson(text) {
    try {
      // First attempt: Try parsing as-is
      JSON.parse(text);
      return text;
    } catch (initialError) {
      console.warn('Initial JSON parsing failed:', initialError);

      let repaired = text;

      // Apply standard JSON repairs first
      try {
        // Common repair strategies (your existing code)
        const repairs = [
          // Ensure object wrapping
          () => {
            if (!repaired.startsWith('{')) repaired = '{' + repaired;
            if (!repaired.endsWith('}')) repaired = repaired + '}';
          },
          // Fix trailing commas
          () => {
            repaired = repaired.replace(/,\s*([}\]])/g, '$1');
          },
          // Add missing quotes around property values
          () => {
            repaired = repaired.replace(
              /:\s*([^"{}[]\s,]+)([,}])/g,
              ': "$1"$2'
            );
          },
          // Balance brackets and braces
          () => {
            const stack = [];
            const pairs = { '{': '}', '[': ']' };
            const chars = repaired.split('');

            chars.forEach((char, i) => {
              if ('{['.includes(char)) {
                stack.push(char);
              } else if ('}]'.includes(char)) {
                if (stack.length === 0 || pairs[stack.pop()] !== char) {
                  chars[i] = '';  // Remove unmatched closing bracket/brace
                }
              }
            });

            // Add missing closing brackets/braces
            while (stack.length > 0) {
              chars.push(pairs[stack.pop()]);
            }

            repaired = chars.join('');
          }
        ];

        // Apply repairs sequentially
        for (const repair of repairs) {
          repair();
          try {
            JSON.parse(repaired);
            return repaired;
          } catch (e) {
            continue;
          }
        }

        // If standard repairs fail, try regex-based extraction as a last resort
        return this.regexExtractConversation(text);

      } catch (e) {
        console.error('All JSON repair attempts failed:', e);
        return this.getFallbackResponse();
      }
    }
  },

  // New regex-based conversation extraction
  regexExtractConversation(text) {
    try {

      // First, check if we can find the conversation array pattern
      const conversationMatch = text.match(/"conversation"\s*:\s*\[(.*)\]/s);

      if (!conversationMatch) {
        console.error('Could not find conversation array pattern');
        return this.getFallbackResponse();
      }

      const conversationContent = conversationMatch[1];

      // Use a similar approach to the backend to extract individual segments
      const segments = [];
      let currentSegment = '';
      let braceCount = 0;
      let inQuotes = false;
      let escapeNext = false;

      for (let i = 0; i < conversationContent.length; i++) {
        const char = conversationContent[i];

        if (escapeNext) {
          escapeNext = false;
          currentSegment += char;
          continue;
        }

        if (char === '\\') {
          escapeNext = true;
          currentSegment += char;
          continue;
        }

        if (char === '"' && !escapeNext) {
          inQuotes = !inQuotes;
        }

        currentSegment += char;

        if (!inQuotes) {
          if (char === '{') {
            braceCount++;
          } else if (char === '}') {
            braceCount--;
            if (braceCount === 0) {
              // We've found a complete segment
              segments.push(currentSegment.trim());
              currentSegment = '';
            }
          }
        }
      }

      // Now we need to parse each segment and extract the relevant fields
      const parsedSegments = [];

      for (const segment of segments) {
        try {
          // Try to extract speaker, role, content, etc. using regex
          const speakerMatch = segment.match(/"speaker"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/);
          const roleMatch = segment.match(/"role"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/);
          const contentMatch = segment.match(/"content"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/);
          const timestampMatch = segment.match(/"timestamp"\s*:\s*([^,}]+)/); // Fixed: removed unnecessary escape
          const sectionMatch = segment.match(/"section"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/);

          if (speakerMatch && roleMatch && contentMatch) {
            parsedSegments.push({
              speaker: this.unescapeJsonString(speakerMatch[1]),
              role: this.unescapeJsonString(roleMatch[1]),
              content: this.unescapeJsonString(contentMatch[1]),
              timestamp: timestampMatch ? parseInt(timestampMatch[1], 10) : 0,
              section: sectionMatch ? this.unescapeJsonString(sectionMatch[1]) : 'main'
            });
          }
        } catch (segmentError) {
          console.warn('Failed to parse segment:', segmentError);
          // Continue with other segments
        }
      }

      if (parsedSegments.length > 0) {
        return JSON.stringify({ conversation: parsedSegments });
      } else {
        console.error('Failed to extract any valid segments');
        return this.getFallbackResponse();
      }
    } catch (e) {
      console.error('Regex extraction failed:', e);
      return this.getFallbackResponse();
    }
  },

  // Helper to unescape JSON string content
  unescapeJsonString(str) {
    return str
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\')
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
      .replace(/\\b/g, '\b')
      .replace(/\\f/g, '\f');
  },

  getFallbackResponse() {
    return JSON.stringify({
      conversation: [{
        speaker: "System",
        role: "Error",
        content: "Failed to parse conversation data. Please try again.",
        timestamp: Date.now()
      }]
    });
  },
  formatCurrency(text) {
    // Replace irregular currency formatting like "$262, 000" with proper format
    return text.replace(/\$\s*(\d+)\s*,\s*(\d+)/g, (match, p1, p2) => {
      return `$${p1},${p2}`;
    });
  },

  cleanContent(content) {
    if (!content) return '';

    try {
      // First preserve [Source: url] pattern
      let cleaned = content.replace(/\[Source:\s*(https?:\/\/[^\]]+)\]/g,
        match => match.replace(/\s+/g, '___SPACE___')
      );

      // Format currency amounts
      cleaned = this.formatCurrency(cleaned);

      // Unescape JSON escapes
      cleaned = cleaned
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');

      // Add spaces after punctuation (but not in currency)
      cleaned = cleaned
        .replace(/([^$]),((?!\d))/g, '$1, $2')  // Add space after comma if not in currency
        .replace(/:(?!\/\/)(\S)/g, ': $1');  // Add space after colon if not in protocol

      // Clean remaining HTML entities
      cleaned = this.decodeHtmlEntities(cleaned);

      // Normalize whitespace (except in preserved sections)
      cleaned = cleaned
        .replace(/\s+/g, ' ')
        .trim();

      // Restore [Source: url] spaces
      cleaned = cleaned.replace(/___SPACE___/g, ' ');

      return cleaned;
    } catch (e) {
      console.error('Error in cleanContent:', e);
      return content || '';
    }
  },
    getTextForSpeech(content) {
    // Remove span tags completely
    return content
      .replace(/<span.*?>(.*?)<\/span>/g, '')  // Remove spans and their content completely
      .replace(/<[^>]+>/g, '')                 // Remove any other HTML tags
      .replace(/\[.*?from dynamic content\]/g, '') // Remove dynamic content placeholders
      .replace(/\[.*?perspective.*?\]/g, '')   // Remove perspective placeholders
      .replace(/\[.*]/g, '')                   // Remove any remaining brackets
      .replace(/&quot;/g, '"')                 // Convert common HTML entities
      .replace(/&amp;/g, '&')
      .replace(/&nbsp;/g, '')              // Remove non-breaking spaces
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\s+/g, ' ')                    // Normalize whitespace
      .trim();
    },
    // Keep existing play/pause/seek methods.
    togglePlay() {
        if (this.isPlaying) {
            this.pause();
        } else {
            this.play();
        }
    },

    skipForward() {
      if (this.activeSegment < this.parsedConversation.length - 1) {
        const wasPlaying = this.isPlaying;
        if (wasPlaying) {
          this.pause();
        }
        this.activeSegment++;

        // Update the current speaker even when not playing
        if (this.parsedConversation[this.activeSegment]) {
          this.currentSpeaker = this.parsedConversation[this.activeSegment].speaker;
        }

        // Only resume playing if it was playing before
        if (wasPlaying) this.play();

        // Update the current segment documents
        this.currentSegmentDocuments = this.getCurrentSegmentDocuments();
      }
      // if skipping to second, pause the intro music
      if (this.activeSegment === 1 && this.introAudio) {
        this.introAudio.pause();
      }
    },

    skipBackward() {
      if (this.activeSegment > 0) {
        const wasPlaying = this.isPlaying;
        if (wasPlaying) {
          this.pause();
        }
        this.activeSegment--;

        // Update the current speaker even when not playing
        if (this.parsedConversation[this.activeSegment]) {
          this.currentSpeaker = this.parsedConversation[this.activeSegment].speaker;
        }

        // Only resume playing if it was playing before
        if (wasPlaying) this.play();

        // Update the current segment documents
        this.currentSegmentDocuments = this.getCurrentSegmentDocuments();
      }
    },

    seek(event) {
      // if still on segment 0, pause the intro music
      if (this.activeSegment === 0) {
        this.introAudio.pause();
      }

      const rect = event.target.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const percentage = x / rect.width;

      // Calculate segment based on percentage of total segments
      const newSegment = Math.floor(percentage * this.parsedConversation.length);
      this.activeSegment = Math.min(newSegment, this.parsedConversation.length - 1);

      if (this.isPlaying) {
          this.pause();
          this.play();
      }
    },
    initAudioContext() {
      try {
        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        // Create audio source
        const source = this.audioContext.createBufferSource();
        source.playbackRate.value = 4.0; // Set playback rate to 1.2x

        // Connect to destination
        source.connect(this.audioContext.destination);

        this.audioPlayer = new Audio();
        this.audioPlayer.addEventListener('timeupdate', this.handleTimeUpdate);
        this.audioPlayer.addEventListener('ended', this.handleAudioEnded);
    } catch (error) {
        console.error('Error initializing AudioContext:', error);
    }
    },
    initMediaSession() {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', () => this.play());
        navigator.mediaSession.setActionHandler('pause', () => this.pause());
        navigator.mediaSession.setActionHandler('previoustrack', () => this.previousSegment());
        navigator.mediaSession.setActionHandler('nexttrack', () => this.nextSegment());
      }
    },

    updateMediaSessionMetadata(segment) {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: `Segment ${this.activeSegment + 1} / ${this.parsedConversation.length}`,
          artist: this.normalizeRole(segment.role),
          album: 'UnStruct.AI Conversation',
          artwork: [
            { src: `https://${window.location.host}/static/unstruct-robot.png`, sizes: '96x96', type: 'image/png' },
            { src: `https://${window.location.host}/static/unstruct-robot.png`, sizes: '128x128', type: 'image/png' },
            { src: `https://${window.location.host}/static/unstruct-robot.png`, sizes: '192x192', type: 'image/png' },
            { src: `https://${window.location.host}/static/unstruct-robot.png`, sizes: '256x256', type: 'image/png' },
            { src: `https://${window.location.host}/static/unstruct-robot.png`, sizes: '384x384', type: 'image/png' },
            { src: `https://${window.location.host}/static/unstruct-robot.png`, sizes: '512x512', type: 'image/png' }
          ]
        });
      }
    },

    async getAudioForSegment(segmentIndex) {
      // First check if we have a custom recording for this segment
      if (this.customRecordings.has(segmentIndex)) {
        const recordingData = this.customRecordings.get(segmentIndex);
        this.isUsingCustomRecording = true;

        // If we have a direct blob (locally recorded in this session)
        if (recordingData.audioBlob) {
          return recordingData.audioBlob;
        }

        // Check cache first for remote recordings
        if (this.customRecordingCache.has(segmentIndex)) {
          return this.customRecordingCache.get(segmentIndex);
        }

        // If we have a URL (previously recorded and stored)
        if (recordingData.audioUrl) {
          try {
            const token = localStorage.getItem("token");
            const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
            const proxy_voice_url = token ? `/user_customization/messages/${this.messageId}/segments/${segmentIndex}/voice-proxy` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/segments/${segmentIndex}/voice-proxy`;
            const response = await API.get(proxy_voice_url, {
              responseType: 'arraybuffer',
              headers: {
                'Accept': 'audio/mpeg',
                'Content-Type': 'application/json'
              }
            });

            // Cache the audio data
            this.customRecordingCache.set(segmentIndex, response.data);

            return response.data;
          } catch (error) {
            console.error(`Failed to fetch custom audio for segment ${segmentIndex}:`, error);
            this.isUsingCustomRecording = false;
            // Fall back to the original TTS method
          }

        }
      }

      // Otherwise, fall back to the original TTS method
      this.isUsingCustomRecording = false;

      const segment = this.parsedConversation[segmentIndex];

      // Get unique voice for THIS speaker (not the currently active speaker)
      const selectedVoice = this.getVoiceForSpeaker(segment.speaker);

      // Check if we have cached audio AND if it's using the correct voice
      if (this.audioCache.has(segmentIndex)) {
        const cachedItem = this.audioCache.get(segmentIndex);
        // Only use cache if the voice matches the currently selected voice
        if (cachedItem.voiceId === selectedVoice) {
          return cachedItem.audioData;
        }
        // Otherwise, we need to re-fetch with the correct voice
      } else {
        // This is a new segment we haven't cached yet
      }

      const text = this.getEnhancedTextForSpeech(segment.content);
      let role;

      if (segment.section === 'break' && segment.role && !segment.role.toLowerCase().includes('host')) {
        role = "UnStruct.AI Sponsor";
      } else {
        role = segment.role || "Default";
      }

      const token = localStorage.getItem("token");
      const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
      const url = token ? '/tts' : `/tts/anonymous/${unstruct_anonymous_key}`;

      try {
        const response = await API.post(url, {
          text,
          provider: this.voiceProvider,
          voice: selectedVoice,
          speed: this.voiceSpeed
        }, {
          responseType: 'arraybuffer',
          headers: {
            'Accept': 'audio/mpeg',
            'Content-Type': 'application/json'
          }
        });

        // Cache both the voice ID and the audio data
        this.audioCache.set(segmentIndex, {
          voiceId: selectedVoice,
          audioData: response.data
        });

        return response.data;
      } catch (error) {
        console.error(`Failed to fetch audio for segment ${segmentIndex}:`, error);
        throw error;
      }
    },
   /**
     * Fetch multiple upcoming segments to improve mobile playback experience
     * @param {number} currentSegmentIndex - The current playing segment index
     * @returns {Promise} - Promise that resolves when prefetching is complete
     */
     async preFetchNextSegments(currentSegmentIndex) {
      // For mobile devices, fetch multiple segments ahead
      const segmentsToFetch = this.isMobile ? 4 : 2;

      // Create array of segment indexes to fetch (avoiding out of bounds)
      const nextSegmentIndexes = Array.from(
        { length: segmentsToFetch },
        (_, i) => currentSegmentIndex + i + 1
      ).filter(idx => idx < this.parsedConversation.length);

      if (nextSegmentIndexes.length === 0) return Promise.resolve();

      try {
        // Fetch all segments in parallel
        return Promise.all(
          nextSegmentIndexes.map(async (idx) => {
            // First check if we have a custom recording for this segment
            if (this.customRecordings.has(idx)) {
              const recordingData = this.customRecordings.get(idx);

              // If we already have the blob in memory, no need to fetch
              if (recordingData.audioBlob) {
                return Promise.resolve();
              }

              // If we only have a URL, prefetch the audio content
              if (recordingData.audioUrl && !this.customRecordingCache?.has(idx)) {
                try {
                  const token = localStorage.getItem("token");
                  const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
                  const proxy_voice_url = token ? `/user_customization/messages/${this.messageId}/segments/${idx}/voice-proxy` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/segments/${idx}/voice-proxy`;
                  const response = await API.get(proxy_voice_url, {
                    responseType: 'arraybuffer',
                    headers: {
                      'Accept': 'audio/mpeg',
                      'Content-Type': 'application/json'
                    }
                  });

                  // Create cache if it doesn't exist
                  if (!this.customRecordingCache) {
                    this.customRecordingCache = new Map();
                  }

                  // Cache the fetched audio data
                  this.customRecordingCache.set(idx, response.data);

                  return Promise.resolve();
                } catch (error) {
                  console.warn(`Failed to prefetch custom recording for segment ${idx}:`, error);
                  // Fall through to TTS if custom recording fetch fails
                }
              }
            }

            // If no custom recording (or fetching it failed), proceed with TTS logic
            const segment = this.parsedConversation[idx];
            const selectedVoice = this.getVoiceForSpeaker(segment.speaker);

            // Check if already cached with the correct voice
            if (this.audioCache.has(idx)) {
              const cachedItem = this.audioCache.get(idx);
              if (cachedItem.voiceId === selectedVoice) {
                return Promise.resolve();
              }
            }

            // If not cached or voice doesn't match, fetch it
            return this.getAudioForSegment(idx);
          })
        );
      } catch (error) {
        console.warn(`Pre-fetch failed for segments after ${currentSegmentIndex}:`, error);
        return Promise.reject(error);
      }
    },

    async play() {
      this.isPlaying = true;
      this.hasStartedPlaying = true;
      await this.startPodcast();
    },

    async startPodcast() {
      const segment = this.parsedConversation[this.activeSegment];
      if (!segment) return;

      // emit event to parent component for tracking
      this.$emit('segment-start', this.activeSegment, this.messageId);

      // start intro music if on segment 0
      if (this.activeSegment === 0) {
        this.playIntroMusic(0.05, "Weirder-Stuff-Geographer.mp3");
      }

      try {
        if (this.audioPlayer.src) {
          this.audioPlayer.pause();
          this.audioPlayer.currentTime = 0;
        }

        // Only show loading if we don't have this segment cached with correct voice
        const hasValidCache = this.audioCache.has(this.activeSegment) &&
          this.audioCache.get(this.activeSegment).voiceId === this.getVoiceForSpeaker(segment.speaker);

        this.isLoading = !hasValidCache;

        // set active speaker
        this.currentSpeaker = segment.speaker;

        const audioData = await this.getAudioForSegment(this.activeSegment);
        const audioBlob = new Blob([audioData], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);

        this.updateMediaSessionMetadata(segment);

        if (this.audioPlayer) {
          this.audioPlayer.src = audioUrl;
          await this.audioPlayer.play();
          this.isLoading = false; // Ensure loading is cleared when audio starts

          // Get previous segment type
          const prevSegment = this.parsedConversation[this.activeSegment - 1];

          // Only start new background music if:
          // 1. No previous segment exists, or
          // 2. Previous segment was a different type
          if (segment.section === 'break' && prevSegment?.section !== 'break') {
            this.playIntroMusic(0.05, "Venice-Beach-Topher-Mohr-and-Alex-Elena.mp3");
          }
          if (segment.section === 'last' && prevSegment?.section !== 'last') {
            this.playIntroMusic(0.05, "Cycles-Audionautix.mp3");
          }
          if (segment.section === 'suspense' && prevSegment?.section !== 'suspense') {
            this.playIntroMusic(0.05, "Distrust-The-System-The-Soundlings.mp3");
          }
          if (segment.section === 'cliffhanger' && prevSegment?.section !== 'cliffhanger') {
            this.playIntroMusic(0.05, "Distrust-The-System-The-Soundlings.mp3");
          }
          if (segment.section === 'revelation' && prevSegment?.section !== 'revelation') {
            this.playIntroMusic(0.05, "Between-The-Spaces-The Soundlings.mp3");
          }

          // CRITICAL FIX: ALWAYS pre-fetch next segment, regardless of cache state
          this.preFetchNextSegments(this.activeSegment)
            .catch(error => {
              // Make sure to catch any errors so they don't affect playback
              console.error('Error in prefetching next segment:', error);
            });
        }
      } catch (error) {
        console.error('Speech playback failed:', error);
        this.isPlaying = false;
        this.isLoading = false;
      }
    },

    pause() {
      this.isPlaying = false;
      if (this.voiceProvider === 'browser') {
          this.speechSynth.cancel();
      } else if (this.audioPlayer) {
          this.audioPlayer.pause();
      }
      // pause the intro music as well
      if (this.introAudio) {
        this.introAudio.pause();
      }
    },

    handleTimeUpdate() {
      this.currentTime = this.audioPlayer.currentTime;
      // Update progress bar or other UI elements
    },

    handleAudioEnded() {
      if (this.audioPlayer.src) {
          URL.revokeObjectURL(this.audioPlayer.src);
      }
      this.handleSegmentEnd();
    },

    previousSegment() {
      if (this.activeSegment > 0) {
          this.activeSegment--;
          this.play();
      }
    },

    nextSegment() {
      if (this.activeSegment < this.parsedConversation.length - 1) {
          this.activeSegment++;
          this.play();
      }
    },
    async downloadFullConversation() {
        if (this.isDownloading) return;

        try {
          this.isDownloading = true;
          this.downloadProgress = 0;
          const allSegments = [];

          // emit event to parent component for tracking
          this.$emit('download-start', this.messageId);

          for (let i = 0; i < this.parsedConversation.length; i++) {
            // Check if we have a custom recording for this segment
            if (this.customRecordings.has(i)) {
              allSegments.push(this.customRecordings.get(i).audioBlob);
            } else {
              // Use cached audio if available, otherwise fetch
              const audioData = await this.getAudioForSegment(i);
              allSegments.push(audioData);
            }
            this.downloadProgress = ((i + 1) / this.parsedConversation.length) * 100;
          }

          // Create final blob
          const blob = new Blob(allSegments, { type: 'audio/mpeg' });
          const url = URL.createObjectURL(blob);

          // Store these for later downloads
          this.downloadUrl = url;
          this.downloadReady = true;

          // emit event to parent component for tracking
          this.$emit('download-end', this.messageId);

        } catch (error) {
          console.error('Download preparation failed:', error);
        } finally {
          this.isDownloading = false;
          this.downloadProgress = 0;
        }
      },

    // Add this simple download method
    triggerDownload() {
      if (!this.downloadUrl) return;

      const a = document.createElement('a');
      a.href = this.downloadUrl;
      a.download = 'unstruct-conversation.mp3';
      a.click();

      // emit event to parent component for tracking
      this.$emit('download-triggered', this.messageId);
    },

    clearAudioCache() {
      this.audioCache.clear();
    },
    clearAudioCacheAndReload() {
      // First clear the audio cache since we'll need new audio with the new voices
      this.clearAudioCache();

      // Then handle any other profile change logic
      // If you need to trigger a re-render or reload of current audio:
      this.reloadCurrentAudio(); // Implement this method if needed
    },
    reloadCurrentAudio() {
      if (this.isPlaying) {
        this.pause();
        this.play();
      }
    },

    isLastSegment() {
      return this.activeSegment === this.parsedConversation.length - 1;
    },
    isSecondSegment() {
      return this.activeSegment === 1;
    },
    handleSegmentEnd() {
      this.showCitationPanel = false;
      this.citationHistory = [];
      this.segmentCitationUrls = [];
      this.citationIdToUrlMap.clear();

      // emit event to parent component for tracking
      this.$emit('segment-end', this.activeSegment, this.messageId);

      if (this.isLastSegment()) {
        this.activeSegment = 0;
        this.isPlaying = false;
        this.currentSegmentDocuments = [];
        this.showDocumentsInsteadOfSpeakers = false;

        // emit event to parent component for tracking
        this.$emit('conversation-end', this.messageId);

        // Pause the intro music and audio player
        if (this.introAudio) {
          this.introAudio.pause();
        }
        if (this.audioPlayer) {
          this.audioPlayer.pause();
        }
      } else {
        const currentType = this.parsedConversation[this.activeSegment]?.section;
        const nextType = this.parsedConversation[this.activeSegment + 1]?.section;

        this.activeSegment++;

        // Update the documents for the next segment
        this.currentSegmentDocuments = this.getCurrentSegmentDocuments();

        this.play();

        // Only pause intro music if transitioning between different section types
        if (this.introAudio && currentType !== nextType &&
            (currentType === 'intro' ||
              currentType === 'break' ||
              currentType === 'last' ||
              currentType === 'suspense' ||
              currentType === 'cliffhanger' ||
              currentType === 'revelation'
            ))
          {
          this.introAudio.pause();
        }
      }
    },
  },
  mounted() {
    this.$nextTick(async () => {
      // Fetch speaker images
      // get audio for first segment
      this.getAudioForSegment(0)
        .then(() => {
          this.isLoading = false;
        })
        .catch((error) => {
          console.error('Error fetching audio for first segment:', error);
          this.isLoading = false;
        });

      await this.fetchSpeakerImages();
      if (this.isSharedView && this.sharedId) {
        const hasPlayed = localStorage.getItem(`played_conversation_${this.sharedId}`);
        if (hasPlayed === 'true') {
          this.hasStartedPlaying = true;

          // Optional: Auto-play if already seen before
          // if (this.autoPlay) {
          //   this.play();
          // }
        }
      }
      // Load speaker customizations first
      await this.fetchSpeakerCustomizations();

      // Then handle other initialization tasks
      if (!this.isPlaying) {
        setTimeout(() => {
          this.makeSegmentsEditable();
        }, 2000);
      }

      // Initialize the current speaker with the first segment's speaker
      if (this.parsedConversation && this.parsedConversation.length > 0) {
        this.currentSpeaker = this.parsedConversation[0].speaker;
      }

      // Process initial citations
      this.processCitationsInSegment();
      this.displaySegmentCitations();

      // Fetch document data
      this.fetchDocumentData();
      this.processDocumentData(this.documentData);

      // Fetch voice recordings
      this.fetchVoiceRecordings();

      this.checkMobile();
    });
  },
  beforeDestroy() {
    this.clearAudioCache();
    this.pause();
    if (this.audioPlayer) {
        this.audioPlayer.removeEventListener('timeupdate', this.handleTimeUpdate);
        this.audioPlayer.removeEventListener('ended', this.handleAudioEnded);
        this.audioPlayer.pause();
        if (this.audioPlayer.src) {
            URL.revokeObjectURL(this.audioPlayer.src);
        }
    }

    if (this.audioContext) {
        this.audioContext.close();
    }
    clearInterval(this.timeInterval);
  }
}
</script>

<style scoped>

/* Base styles for the conversation player */
.conversation-player {
  position: relative;
  max-width: 1800px;
  margin: auto;
  border-radius: 0.5rem;
}

.player-layout {
  display: flex;
  height: 100%;
}

/* Layout columns */
.metadata-column {
  width: 600px;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  overflow-y: auto;
}

.speaker-column {
  width: 400px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.conversation-column {
  flex: 1;
  overflow-y: auto;
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.conversation-column.is-playing {
    display: none;
  }

.conversation-column::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.speaker-column.is-playing {
  width: 100%;
  margin-left: 0;
}

/* Conversation container */
.conversation {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 425px;
  overflow-y: auto;
  position: relative;
  scroll-behavior: smooth;
  padding-left: 1rem;
}

/* Segment styling */
.segment {
  padding: 0.5rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  border-left: 4px solid transparent;
  position: relative;
  transition: all 0.3s ease;
}

.segment.active {
  border-left-color: #284b7c;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Theme variations */
.theme--light .segment.active {
  background: #f7fafc;
  border-left-color: #4299e1;
}

.theme--dark .segment.active {
  background: #284b7c;
}

/* Speaker info styles */
.speaker-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.speaker-name {
  color: #4299e1;
  font-weight: 600;
}

.speaker-role {
  color: #a0aec0;
  font-size: 0.875rem;
}

/* Player controls */
.player-header {
  display: flex;
  justify-content: center;
  align-items: center;
}

.player-controls {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  justify-content: center;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Playing indicator */
.playing-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 1rem;
  gap: 0.5rem;
}

.wave-bars {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 12px;
}

.wave-bar {
  width: 2px;
  height: 100%;
  background-color: var(--wave-color, #4299e1);
  border-radius: 1px;
  animation: soundBars 1.2s ease-in-out infinite;
}

.wave-bar:nth-child(1) { animation-delay: 0s; }
.wave-bar:nth-child(2) { animation-delay: 0.2s; }
.wave-bar:nth-child(3) { animation-delay: 0.4s; }

@keyframes soundBars {
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
}

/* Loading indicator */
.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: #00eaff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #6b7280;
  font-size: 0.875rem;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* YouTube toggle button */
.youtube-toggle {
  display: flex;
  justify-content: center;
}

.youtube-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background-color: #f0f0f0;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  color: #606060;
}

.youtube-toggle-btn.active {
  background-color: #ff0000;
  color: white;
}

.youtube-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ff0000;
  color: white;
  font-size: 12px;
  font-weight: bold;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

/* Empty state placeholder */
.empty-state-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.placeholder-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 500px;
}

.placeholder-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  opacity: 0.9;
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 0.9; }
}

/* Edit indicator */
.modern-edit-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  margin-left: 8px;
  cursor: pointer;
  color: #e53e3e;
  background-color: rgba(66, 153, 225, 0.1);
  transition: all 0.2s ease;
  vertical-align: middle;
}

.modern-edit-indicator:hover {
  background-color: rgba(66, 153, 225, 0.2);
  transform: scale(1.05);
}

/* Custom voice badge */
.custom-voice-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #ED8936;
  color: white;
  border-radius: 9999px;
  width: 20px;
  height: 20px;
  font-size: 12px;
  margin-left: 8px;
}

/* =========== RESPONSIVE BREAKPOINTS =========== */

/* Large screens (standard desktop) - default styles apply */

/* Medium-large screens (smaller desktops and laptops, 1366px) */
@media screen and (max-width: 1366px) {
  .conversation-player {
    max-width: 100%;
  }

  .metadata-column {
    width: 350px;
    min-width: 250px;
  }

  .speaker-column {
    width: 500px;
  }

  .conversation {
    height: 400px;
  }
}

/* Medium screens (tablets in landscape, smaller laptops, 1024px) */
@media screen and (max-width: 1024px) {
  .conversation-player {
    max-width: 100%;
  }

  .metadata-column {
    width: 300px;
    min-width: 200px;
  }

  .speaker-column {
    width: 400px;
    padding-left: 1rem;
  }

  .conversation-column {
    padding: 0 0.5rem;
  }

  .conversation {
    height: 375px;
    padding-left: 0.5rem;
  }

  .segment {
    padding: 0.4rem;
    font-size: 0.85rem;
  }

  .player-controls {
    gap: 1rem;
  }
}

/* Medium-small screens (tablets in portrait, 768px) */
@media screen and (max-width: 900px) {
  .player-layout {
    flex-direction: column;
  }

  .metadata-column,
  .speaker-column,
  .conversation-column {
    width: 100%;
    padding: 0.5rem;
  }

  .metadata-column.is-expanded,
  .speaker-column.is-expanded {
    width: 100%;
    display: block;
  }

  .speaker-column.is-playing {
   margin-bottom: 6rem;
   padding-bottom: 2rem;
  }

  .speaker-column {
    order: 1;
  }

  .conversation-column {
    order: 2;
  }

  .metadata-column {
    order: 0;
    position: relative;
  }

  .conversation {
    height: 350px;
  }
}

/* Small screens (mobile devices, 480px) */
@media screen and (max-width: 768px) {
  .conversation-player {
    margin: 5px auto;
    padding: 0 0.3rem;
  }

  .player-layout {
    flex-direction: column;
  }

  .metadata-column,
  .speaker-column,
  .conversation-column {
    width: 100%;
    min-width: 100%;
    padding: 0;
    margin: 0;
    position: static;
  }

  .conversation {
    height: 325px;
    padding-left: 0;
  }

  .segment {
    width: 100%;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .player-controls {
    width: 100%;
    margin: 0.5rem 0;
    flex-wrap: wrap;
    justify-content: center;
  }

  .player-header {
    flex-direction: column;
    align-items: center;
  }

  .control-group {
    justify-content: center;
    width: 100%;
  }

  .rate-control {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin: 0.25rem 0;
  }

  .rate-btn {
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
  }

  .speaker-info {
    flex-wrap: wrap;
  }

  .progress-counter {
    top: -10px;
  }

  /* Ensure touch targets are large enough */
  .control-btn,
  .play-btn {
    min-height: 44px;
    min-width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .youtube-toggle-btn {
    padding: 6px 10px;
  }

  .youtube-count {
    min-width: 18px;
    height: 18px;
    font-size: 10px;
  }

  /* Mobile voice recorder toggle */
  .voice-recorder-mobile-toggle {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: #ED8936;
    color: white;
    font-size: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 100;
    border: none;
  }
}

/* Extra small screens (small mobile devices, 375px) */
@media screen and (max-width: 375px) {
  .conversation {
    height: 300px;
  }

  .segment {
    padding: 0.5rem;
    margin-bottom: 0.5rem;
    font-size: 0.8rem;
  }

  .speaker-name,
  .speaker-role {
    font-size: 0.8rem;
  }

  .modern-edit-indicator,
  .custom-voice-badge {
    width: 18px;
    height: 18px;
    font-size: 10px;
  }

  .playing-indicator,
  .loading-indicator {
    margin-left: 0.5rem;
    font-size: 0.7rem;
  }
}

.segment-transcript-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 5;
}

/* Speaker image overlay when in playing mode */
.conversation-player.is-playing-mode .speaker-column {
  position: relative;
  padding-top: 80px; /* Make room for the speaker image overlay */
}

/* Create the speaker image overlay container */
.conversation-player.is-playing-mode .speaker-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 70px;
  display: flex;
  align-items: center;
  padding: 0 1rem;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px 8px 0 0;
  z-index: 5;
}

/* Dark mode support */
.theme--dark .conversation-player.is-playing-mode .speaker-image-overlay {
  background: rgba(30, 41, 59, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Speaker image styling */
.conversation-player.is-playing-mode .speaker-image {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #4299e1;
  margin-right: 1rem;
}

/* Active speaker info styling */
.conversation-player.is-playing-mode .speaker-info-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.conversation-player.is-playing-mode .speaker-name {
  font-weight: 600;
  font-size: 1rem;
  color: #2d3748;
}

.conversation-player.is-playing-mode .speaker-role {
  font-size: 0.85rem;
  color: #718096;
}

/* Dark mode text colors */
.theme--dark .conversation-player.is-playing-mode .speaker-name {
  color: #e2e8f0;
}

.theme--dark .conversation-player.is-playing-mode .speaker-role {
  color: #a0aec0;
}

/* Speaker wave animation */
.conversation-player.is-playing-mode .speaker-wave {
  display: flex;
  align-items: center;
  margin-left: auto;
  height: 20px;
}

.conversation-player.is-playing-mode .wave-bar {
  display: inline-block;
  width: 3px;
  height: 100%;
  margin-right: 2px;
  background-color: #4299e1;
  border-radius: 3px;
  animation: speakerWave 1.2s ease-in-out infinite;
}

.conversation-player.is-playing-mode .wave-bar:nth-child(1) { animation-delay: 0s; }
.conversation-player.is-playing-mode .wave-bar:nth-child(2) { animation-delay: 0.2s; }
.conversation-player.is-playing-mode .wave-bar:nth-child(3) { animation-delay: 0.4s; }

@keyframes speakerWave {
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
}

/* Adjustments for mobile */
@media (max-width: 768px) {
  .conversation-player.is-playing-mode .speaker-column {
    padding-top: 70px;
  }

  .conversation-player.is-playing-mode .speaker-image-overlay {
    height: 60px;
  }

  .conversation-player.is-playing-mode .speaker-image {
    width: 40px;
    height: 40px;
  }

  .conversation-player.is-playing-mode .speaker-name {
    font-size: 0.9rem;
  }

  .conversation-player.is-playing-mode .speaker-role {
    font-size: 0.75rem;
  }
}

/* Adjust content panels to work with speaker image overlay */

/* Citation panel adjustments */
.conversation-player.is-playing-mode .citation-panel {
  margin-top: 10px !important;
  border-radius: 0 0 8px 8px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

/* Document display adjustments */
.conversation-player.is-playing-mode .document-display {
  margin-top: 10px !important;
  border-radius: 0 0 8px 8px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

/* Image panel adjustments */
.conversation-player.is-playing-mode .segment-image-panel {
  margin-top: 10px !important;
  border-radius: 0 0 8px 8px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

/* YouTube panel adjustments */
.conversation-player.is-playing-mode .you-tube-panel {
  margin-top: 10px !important;
  border-radius: 0 0 8px 8px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

/* Empty state placeholder adjustments */
.conversation-player.is-playing-mode .empty-state-placeholder {
  margin-top: 10px !important;
  border-radius: 0 0 8px 8px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

/* Add some flair to make content panels look connected to the speaker overlay */
.conversation-player.is-playing-mode .citation-panel,
.conversation-player.is-playing-mode .document-display,
.conversation-player.is-playing-mode .segment-image-panel,
.conversation-player.is-playing-mode .you-tube-panel,
.conversation-player.is-playing-mode .empty-state-placeholder {
  border-top: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Add a subtle highlight to the active speaker's content */
.conversation-player.is-playing-mode .speaker-image-overlay::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 25px;
  width: 20px;
  height: 10px;
  background-color: inherit;
  clip-path: polygon(0 0, 100% 0, 50% 100%);
  z-index: 6;
}

.segment-number {
  font-size: 0.75rem;
  color: rgba(128, 128, 128, 0.7);
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  font-weight: normal;
}

</style>
